/**
 * Ultra-Advanced Bank Templates Library v2.0
 * 
 * 40+ Premium Templates including:
 * - Major US Banks (BOFA, Chase, Wells, Citi, etc.)
 * - Regional Banks (Regions, Truist, BMO, Fifth Third, etc.)
 * - Credit Unions (Navy FCU, PenFed, BECU, etc.)
 * - Specialty Checks (Cashier's, Payroll, eCheck, Money Order, etc.)
 * - International (Canadian, UK style)
 */

export interface BankTemplate {
    name: string;
    specs: {
        dimensions: { width: number; height: number; unit: string };
        margins: { top: number; bottom: number; left: number; right: number };
        micrPosition: { fromBottom: number; fromLeft: number };
        fontFamily: string;
        colors: {
            primary: string;
            secondary: string;
            accent?: string;
            background: string;
        };
        securityFeatures: string[];
    };
    fields?: Array<{
        name: string;
        x: number;
        y: number;
        width: number;
        fontSize?: number;
        bold?: boolean;
    }>;
}

export const bankSpecificTemplates: Record<string, BankTemplate> = {

    // ============== MAJOR US BANKS ==============

    BOFA: {
        name: 'Bank of America Premium Business',
        specs: {
            dimensions: { width: 3.5, height: 8.5, unit: 'inches' },
            margins: { top: 0.25, bottom: 0.25, left: 0.25, right: 0.25 },
            micrPosition: { fromBottom: 0.25, fromLeft: 0.625 },
            fontFamily: 'Helvetica Neue',
            colors: {
                primary: '#012169',
                secondary: '#D52B1E',
                background: '#FFFFFF'
            },
            // Catalogue rebuild (fidelity MAJOR wave, 2026-11-05):
            // the evidenced HC ground is the safety treatment, so
            // TEMPLATE_DESIGN moves cloud_marble -> 'security'
            // (marble is Deluxe premium/generic stock; the marble
            // build the owner's specimen carried is preserved as
            // BOFA_CLASSIC_STUDIO, and BOFA_CLASSIC itself was
            // rebuilt to this same evidenced design under the
            // owner ruling of 2026-11-05). The colourway already IS
            // the published identity — navy wordmark (#012169 is
            // BofA's own published institutional blue) with the red
            // flag as accent — so the tokens stand. The pre-rebuild
            // build is preserved as BOFA_STUDIO.
            securityFeatures: ['security-weave', 'microprinting', 'security-screentone', 'fugitive-ink']
        },
        fields: [
            { name: 'payee', x: 0.75, y: 2.0, width: 4.0 },
            { name: 'amount_numbers', x: 5.5, y: 2.0, width: 1.5 },
            { name: 'amount_words', x: 0.75, y: 2.5, width: 6.25 },
            { name: 'date', x: 5.5, y: 0.75, width: 1.5 },
            { name: 'check_number', x: 6.75, y: 0.75, width: 0.75 },
            { name: 'memo', x: 0.75, y: 3.0, width: 2.5 },
            { name: 'signature', x: 4.5, y: 3.25, width: 2.5 },
            { name: 'bank_name', x: 0.75, y: 0.5, width: 3.0, fontSize: 14, bold: true },
            { name: 'bank_address', x: 0.75, y: 0.75, width: 3.0, fontSize: 9 },
            { name: 'account_holder', x: 0.75, y: 1.25, width: 3.0, fontSize: 11 },
            { name: 'account_address', x: 0.75, y: 1.5, width: 3.0, fontSize: 9 }
        ]
    },

    BOFA_CLASSIC: {
        name: 'Bank of America Classic',
        specs: {
            dimensions: { width: 3.5, height: 8.5, unit: 'inches' },
            margins: { top: 0.2, bottom: 0.2, left: 0.2, right: 0.2 },
            micrPosition: { fromBottom: 0.3125, fromLeft: 0.625 },
            fontFamily: 'Helvetica Neue',
            colors: {
                primary: '#012169',
                secondary: '#D52B1E',
                background: '#FFFFFF'
            },
            // Catalogue rebuild under OWNER RULING (Princewill,
            // 2026-11-05: "use there bank original template
            // designs"). The old build was the MARBLE design, which
            // rested on the owner's specimen photo — and marble is
            // Deluxe premium / generic stock, (c) NOT FOUND as a
            // BofA/HC design. No distinct BofA "classic" design
            // exists in the evidence either: BofA's printer is
            // Harland Clarke (a, checks.bankofamerica.com), the
            // customer picks from the HC catalogue, and BofA's own
            // ordering FAQ names only "standard" vs "non-standard"
            // styles (a). So — the CHASE_PRIVATE precedent —
            // BOFA_CLASSIC carries the ONE evidenced BofA original
            // design, the same catalogue design the MAJOR wave
            // rebuilt BOFA to: safety ground on white safety
            // paper, navy wordmark, red accent, HC Standard
            // Personal set. The template keeps its own record
            // (margins, MICR seat), its bofa_classic layout and
            // furniture, and its V8 personal back, whose panel now
            // follows this corrected set. The specimen marble
            // build is preserved verbatim as BOFA_CLASSIC_STUDIO
            // below.
            securityFeatures: ['security-weave', 'microprinting', 'security-screentone', 'fugitive-ink']
        }
    },

    // The pre-rebuild BOFA_CLASSIC build (the owner's specimen
    // marble design), preserved VERBATIM under the same owner
    // ruling — design tokens, palette (incl. the platinum accent),
    // Courier imprint face, record fields and the marble spec list
    // are the pre-rebuild values exactly, so its V8 back keeps the
    // marble panel treatment (explicit BANK_BACK entry, the
    // BOFA_STUDIO precedent). It rides the same bofa_classic
    // layout as the main, as it did before the rebuild.
    BOFA_CLASSIC_STUDIO: {
        name: 'Bank of America Classic Marble (Studio Variant)',
        specs: {
            dimensions: { width: 3.5, height: 8.5, unit: 'inches' },
            margins: { top: 0.2, bottom: 0.2, left: 0.2, right: 0.2 },
            micrPosition: { fromBottom: 0.3125, fromLeft: 0.625 },
            fontFamily: 'Courier New',
            colors: {
                primary: '#012169',
                secondary: '#004A99',
                accent: '#E5E4E2',
                background: '#F0F4F8'
            },
            securityFeatures: ['vertical-security-bar', 'marble-cloud-texture', 'microprint-signature', 'secure-padlock', 'rainbow-border']
        }
    },

    BOFA_BUSINESS_V2: {
        name: 'Bank of America Business (v2)',
        specs: {
            dimensions: { width: 3.5, height: 8.5, unit: 'inches' },
            margins: { top: 0.2, bottom: 0.2, left: 0.2, right: 0.2 },
            micrPosition: { fromBottom: 0.25, fromLeft: 0.625 },
            fontFamily: 'Arial',
            colors: {
                primary: '#012169', // Institutional Blue
                secondary: '#da291c', // BofA Red
                accent: '#666666',
                background: '#FFFFFF'
            },
            securityFeatures: ['vertical-bofa-security-strip', 'square-hologram', 'watermark-seal', 'microprint-border']
        }
    },

    US_TREASURY_OFFICIAL: {
        name: 'United States Treasury (Standard)',
        specs: {
            dimensions: { width: 3.5, height: 8.5, unit: 'inches' },
            margins: { top: 0.1, bottom: 0.1, left: 0.1, right: 0.1 },
            micrPosition: { fromBottom: 0.4, fromLeft: 1.2 },
            fontFamily: 'Courier New',
            colors: {
                primary: '#1e3932',
                secondary: '#212121',
                accent: '#d4af37',
                background: '#f4f8f0'
            },
            // SPECIALTY wave (2026-11-05): the Bureau of the Fiscal
            // Service's published feature list (a: "U.S. Treasury
            // Check Security Features", via legalclarity) names
            // MICROPRINTING in three areas of the check, the front
            // included — so 'microprinting' joins the list and the
            // microprint feature now renders. The other strings are
            // the instrument's artwork / print-process facts (the
            // Statue of Liberty face image, the Treasury seal's
            // bleeding ink, iris printing) and stay in
            // templateSecurity's UNMAPPED artwork/printProcess
            // classes — they claim no rendered paper feature. The
            // design tokens stand: no graded evidence contradicts
            // the fiber ground, courier face or treasury-green
            // paper, so NO (Studio Variant) is created (the BMO
            // precedent — no effective-design field changes). The
            // V6 BFS back stands as-is. The OPEN OWNER QUESTION
            // about specimen strings on a blank Treasury check is
            // NOT touched by this wave: the blank-rule behaviour is
            // exactly as it was.
            securityFeatures: ['statue-of-liberty', 'treasury-seal', 'shaded-signature-box', 'iris-printing', 'bleeding-ink-micr', 'microprinting']
        }
    },

    CHASE: {
        name: 'Chase Business Elite',
        specs: {
            dimensions: { width: 3.5, height: 8.5, unit: 'inches' },
            margins: { top: 0.1875, bottom: 0.1875, left: 0.1875, right: 0.1875 },
            micrPosition: { fromBottom: 0.3125, fromLeft: 0.5 },
            fontFamily: 'Gotham',
            colors: {
                primary: '#117ACA',
                secondary: '#1D428A',
                accent: '#FFC72C',
                background: '#FFFFFF'
            },
            securityFeatures: ['chemical-sensitive-paper', 'microprint-border', 'security-screentone', 'erasure-protection']
        }
    },
    CHASE_PRIVATE: {
        name: 'Chase Private Client (Heritage)',
        specs: {
            dimensions: { width: 3.5, height: 8.5, unit: 'inches' },
            margins: { top: 0.2, bottom: 0.2, left: 0.2, right: 0.2 },
            micrPosition: { fromBottom: 0.3125, fromLeft: 0.625 },
            fontFamily: 'Gotham',
            colors: {
                primary: '#117ACA',
                // Catalogue rebuild (fidelity MAJOR wave, 2026-11-05):
                // no distinct Private Client design exists in the
                // evidence (programme research (c) NOT FOUND), so the
                // template carries the one evidenced Chase catalogue
                // colourway — Chase blue name, heritage navy
                // secondary, the CHASE accent. The gold/cream
                // prestige build was invented trim; it is preserved
                // verbatim as CHASE_PRIVATE_STUDIO.
                secondary: '#1D428A',
                accent: '#FFC72C',
                background: '#FFFFFF'
            },
            securityFeatures: ['chemical-sensitive-paper', 'microprint-border', 'security-screentone', 'erasure-protection']
        }
    },

    WELLS: {
        name: 'Wells Fargo Executive',
        specs: {
            dimensions: { width: 3.5, height: 8.5, unit: 'inches' },
            margins: { top: 0.25, bottom: 0.25, left: 0.25, right: 0.25 },
            micrPosition: { fromBottom: 0.375, fromLeft: 0.75 },
            fontFamily: 'Whitney',
            colors: {
                // Catalogue rebuild (fidelity MAJOR wave, 2026-11-05):
                // Wells Fargo's published identity is RED (wordmark)
                // + yellow on a red box; the old primary #F26D21 is
                // an orange that belongs to no Wells Fargo palette.
                // Red #D41C2C per the published Wells Fargo colour
                // palette (b). The yellow is NOT taken as secondary:
                // the secondary paints the address line, and in the
                // one-colour imprint reality the bank prints red
                // only — the neutral dark secondary stands.
                primary: '#D41C2C',
                secondary: '#231F20',
                background: '#FFFFFF'
            },
            securityFeatures: ['security-weave', 'microprinting', 'security-screentone', 'fugitive-ink']
        }
    },

    CITI: {
        name: 'CitiBusiness Professional',
        specs: {
            dimensions: { width: 3.5, height: 8.5, unit: 'inches' },
            margins: { top: 0.25, bottom: 0.25, left: 0.25, right: 0.25 },
            micrPosition: { fromBottom: 0.3125, fromLeft: 0.625 },
            fontFamily: 'Futura',
            colors: {
                primary: '#003B70',
                secondary: '#EF3E42',
                accent: '#00B3B0',
                background: '#FFFFFF'
            },
            // Catalogue rebuild (fidelity MAJOR wave, 2026-11-05):
            // TEMPLATE_DESIGN moves geometric_diamond -> 'security'
            // (the evidenced Deluxe standard-tier ground). The
            // colourway stands — #003B70 is the Citi blue family
            // with the red arc as accent. The pre-rebuild build is
            // preserved as CITI_STUDIO.
            securityFeatures: ['chemical-sensitive-paper', 'microprint-border', 'security-screentone', 'erasure-protection']
        }
    },

    USAA: {
        name: 'USAA Federal Savings Bank',
        specs: {
            dimensions: { width: 3.5, height: 8.5, unit: 'inches' },
            margins: { top: 0.25, bottom: 0.25, left: 0.25, right: 0.25 },
            micrPosition: { fromBottom: 0.25, fromLeft: 0.5 },
            // Font (2026-11-04 close-look pass): the face's print is
            // a bold CONDENSED grotesque (the Trade Gothic character
            // of the specimen's bank block, holder line, check number
            // and fraction). 'Trade Gothic' alone resolved on almost
            // no system and fell back unpredictably, so the stack
            // names it first (exact where installed) and falls back
            // to the metric condensed faces systems actually carry.
            fontFamily: "'Trade Gothic', 'Arial Narrow', 'Liberation Sans Narrow', Arial, sans-serif",
            colors: {
                primary: '#0a2d5c',
                secondary: '#33507e',
                background: '#FFFFFF'
            },
            // Specimen fingerprint (2026-11-04): stark white face,
            // bold navy double rules top/bottom (TEMPLATE_BORDER),
            // MICR with the TRANSIT/ACCOUNT captions (the standard
            // treatment), microprint signature line, and the printed
            // "Photo Safe Deposit - Details on back" mark the renderer
            // draws as this template's furniture. The bank block is
            // the renderer's bottom-left templateBankBlock furniture.
            // Real-back extension (2026-11-04, check-backs-specimen-
            // research-2026-11-04.md [S1]): a scanned real USAA back
            // (public county record) prints a security list naming the
            // Security Weave pattern on the back and a Chemical Wash
            // Detection Box, so the design honestly carries
            // 'security-weave' (specs-only panel wording) and
            // 'chemical-reactive' (chemical feature -> panel line +
            // the wash box on ITS back). Fugitive ink is also named
            // on the real back; the studio has no such feature, so it
            // is recorded in the research file, not modelled.
            securityFeatures: ['magnetic-ink-micr', 'microprint-border', 'microprint-signature', 'photo-safe-deposit', 'security-weave', 'chemical-reactive']
        }
    },

    GREENFI: {
        name: 'GreenFi',
        specs: {
            dimensions: { width: 3.5, height: 8.5, unit: 'inches' },
            margins: { top: 0.25, bottom: 0.25, left: 0.25, right: 0.25 },
            micrPosition: { fromBottom: 0.25, fromLeft: 0.5 },
            fontFamily: 'Arial',
            colors: {
                primary: '#2f9e44',
                secondary: '#5aa469',
                accent: '#d94f3d',
                background: '#f4faf0'
            },
            // Specimen fingerprint (EXACT rebuild, 2026-11-04):
            // smooth near-white paper with only a touch of light
            // green (the first build's mint + linen grid was
            // rejected by the owner), the FSC write-up IS the frame
            // (TEMPLATE_BORDER MICROPRINT_FRAME in pale green), the
            // four-cell SECURITY SQUARE with the purple key at the
            // left of the pay-to row (renderer furniture — this
            // design carries NO circular seal/medallion), the pale
            // pink heat-reactive padlock right of DOLLARS with its
            // arrow + "Heat Reactive Ink" label, the two-tone
            // rounded wordmark low-left, microprint signature line,
            // and the security statement carried as a printed
            // bottom line instead of the top warning strip.
            securityFeatures: ['heat-reactive', 'microprint-border', 'microprint-signature', 'watermark', 'bottom-security-statement']
        }
    },

    GREENFI_MINT: {
        name: 'GreenFi Mint (Studio Variant)',
        specs: {
            dimensions: { width: 3.5, height: 8.5, unit: 'inches' },
            margins: { top: 0.25, bottom: 0.25, left: 0.25, right: 0.25 },
            micrPosition: { fromBottom: 0.25, fromLeft: 0.5 },
            fontFamily: 'Arial',
            colors: {
                primary: '#2f9e44',
                secondary: '#5aa469',
                accent: '#d94f3d',
                background: '#eaf6ec'
            },
            // The FIRST GreenFi build (mint paper, linen weave,
            // strong frame colour, generic seal), preserved as its
            // own library entry per Princewill's disposition
            // (2026-11-04) before the exact rebuild took the
            // GreenFi name. Its design tokens live here +
            // TEMPLATE_DESIGN / TEMPLATE_BORDER under this id.
            securityFeatures: ['heat-reactive', 'microprint-border', 'microprint-signature', 'watermark', 'bottom-security-statement']
        }
    },

    ANB: {
        name: 'ANB Bank',
        specs: {
            dimensions: { width: 3.5, height: 8.5, unit: 'inches' },
            margins: { top: 0.25, bottom: 0.25, left: 0.25, right: 0.25 },
            micrPosition: { fromBottom: 0.25, fromLeft: 0.5 },
            fontFamily: 'Arial',
            colors: {
                primary: '#1b2a5b',
                secondary: '#7d94c8',
                accent: '#c8a415',
                background: '#fdf6d8'
            },
            // Specimen fingerprint (2026-11-04; EXACT rebuild same
            // day after the owner's side-by-side rejection): the
            // face is the Deluxe scenic — pale cloud sky across the
            // TOP, golden wheat field with a fine grain pattern
            // across the BOTTOM (the scenic_field texture); BLACK
            // wordmark block bottom-left with the striped flag mark
            // and the web | phone | Member FDIC line (renderer
            // furniture); swash-script labels (Date / Pay to /
            // Dollars / Memo) in warm-grey ink; OUTLINE CPSA padlock
            // by DOLLARS with its three-line microprint note; a
            // microprint signature line — only what the specimen
            // carries.
            securityFeatures: ['secure-padlock', 'microprint-signature']
        }
    },

    ANB_SKY: {
        name: 'ANB Bank Sky (Studio Variant)',
        specs: {
            dimensions: { width: 3.5, height: 8.5, unit: 'inches' },
            margins: { top: 0.25, bottom: 0.25, left: 0.25, right: 0.25 },
            micrPosition: { fromBottom: 0.25, fromLeft: 0.5 },
            fontFamily: 'Arial',
            colors: {
                primary: '#1b2a5b',
                secondary: '#7d94c8',
                accent: '#c8a415',
                background: '#fdf6d8'
            },
            // The FIRST ANB build (pale yellow-cream paper, soft
            // blue cloud band mid-face, navy wordmark), preserved
            // as its own library entry per Princewill's disposition
            // (2026-11-04) before the exact rebuild took the ANB
            // name. Its design tokens live here + TEMPLATE_DESIGN /
            // TEMPLATE_BORDER under this id.
            securityFeatures: ['secure-padlock', 'microprint-signature']
        }
    },

    PNC: {
        name: 'PNC Business Elite',
        specs: {
            dimensions: { width: 3.5, height: 8.5, unit: 'inches' },
            margins: { top: 0.25, bottom: 0.25, left: 0.25, right: 0.25 },
            micrPosition: { fromBottom: 0.3125, fromLeft: 0.625 },
            fontFamily: 'Helvetica',
            colors: {
                // Catalogue rebuild (fidelity MAJOR wave, 2026-11-05):
                // PNC's published identity is BLUE "PNC" letters with
                // an ORANGE roundel mark (b: logo references agree).
                // The old assignment painted the wordmark orange —
                // the roles are corrected using the template's own
                // existing palette (no new hex introduced): the
                // deep blue-slate becomes the wordmark ink (primary)
                // and the orange becomes the mark/accent (secondary).
                // OWNER RULING follow-up (2026-11-05): the name now
                // prints in the published letter colour itself —
                // "Super Blue" #002FCD, named identically by two
                // published logo references (b: companylogos and
                // schemecolor, the latter with Pantone 2728 C) —
                // via TEMPLATE_WORDMARK_INK (designPresets); the
                // slate primary stays the design's dark ink for its
                // other elements, and the orange stays the
                // roundel/accent.
                primary: '#1D242D',
                secondary: '#FF6600',
                accent: '#FFFFFF',
                background: '#FFFFFF'
            },
            securityFeatures: ['security-weave', 'microprinting', 'security-screentone', 'fugitive-ink']
        }
    },

    TD: {
        name: 'TD Bank Professional',
        specs: {
            dimensions: { width: 3.5, height: 8.5, unit: 'inches' },
            margins: { top: 0.25, bottom: 0.25, left: 0.25, right: 0.25 },
            micrPosition: { fromBottom: 0.3125, fromLeft: 0.5 },
            fontFamily: 'Arial',
            colors: {
                primary: '#34A853',
                secondary: '#00563F',
                background: '#FFFFFF'
            },
            securityFeatures: ['security-weave', 'microprinting', 'security-screentone', 'fugitive-ink', 'uv-dull-paper']
        }
    },

    CAPITAL_ONE: {
        name: 'Capital One Premium',
        specs: {
            dimensions: { width: 3.5, height: 8.5, unit: 'inches' },
            margins: { top: 0.25, bottom: 0.25, left: 0.25, right: 0.25 },
            micrPosition: { fromBottom: 0.25, fromLeft: 0.625 },
            fontFamily: 'Proxima Nova',
            colors: {
                // Catalogue rebuild (fidelity MAJOR wave, 2026-11-05):
                // Capital One's published identity is a NAVY wordmark
                // with a RED swoosh. The old assignment painted the
                // wordmark red — the roles are corrected within the
                // template's own published pair: navy #004879 becomes
                // the wordmark ink (primary), red #D03027 the
                // swoosh/accent (secondary).
                primary: '#004879',
                secondary: '#D03027',
                accent: '#FFFFFF',
                background: '#FFFFFF'
            },
            securityFeatures: ['security-weave', 'microprinting', 'security-screentone', 'fugitive-ink']
        }
    },

    US_BANK: {
        name: 'US Bank Commercial',
        specs: {
            dimensions: { width: 3.5, height: 8.5, unit: 'inches' },
            margins: { top: 0.25, bottom: 0.25, left: 0.25, right: 0.25 },
            micrPosition: { fromBottom: 0.3125, fromLeft: 0.625 },
            fontFamily: 'Avenir',
            colors: {
                primary: '#0C2340',
                secondary: '#D71920',
                background: '#FFFFFF'
            },
            // Catalogue rebuild (fidelity MAJOR wave, 2026-11-05):
            // TEMPLATE_DESIGN moves laser_security -> 'security'
            // (the evidenced Deluxe standard-tier ground). The
            // colourway stands — navy wordmark (#0C2340 is U.S.
            // Bank's published navy family) with the red accent.
            // The pre-rebuild build is preserved as US_BANK_STUDIO.
            securityFeatures: ['chemical-sensitive-paper', 'microprint-border', 'security-screentone', 'erasure-protection']
        }
    },

    HUNTINGTON: {
        name: 'Huntington Business',
        specs: {
            dimensions: { width: 3.5, height: 8.5, unit: 'inches' },
            margins: { top: 0.25, bottom: 0.25, left: 0.25, right: 0.25 },
            micrPosition: { fromBottom: 0.3125, fromLeft: 0.625 },
            fontFamily: 'Georgia',
            colors: {
                primary: '#006747',
                secondary: '#A5A5A5',
                background: '#FFFFFF'
            },
            // Catalogue rebuild (fidelity REGIONAL wave 1, 2026-11-05):
            // Harland Clarke is Huntington's check printer (a,
            // huntington.com). Business template: the evidenced
            // original is the HC Business catalogue design done
            // completely — Security Weave safety ground ('security'
            // texture; the old 'parchment' was a decorative paper
            // no safety stock carries) and the bank imprint in
            // Huntington Green (#006747, the bank's brand green).
            // Security set: HC Business Standard (wave-1
            // correction). No bank-specific safety tint or
            // furniture is evidenced → no-evidence-standard
            // (ledger). The previous build is preserved as
            // HUNTINGTON_STUDIO.
            securityFeatures: ['security-weave', 'microprinting', 'security-screentone', 'fugitive-ink', 'chemical-reactive-paper', 'uv-dull-paper']
        }
    },

    FIFTH_THIRD: {
        name: 'Fifth Third Bank',
        specs: {
            dimensions: { width: 3.5, height: 8.5, unit: 'inches' },
            margins: { top: 0.25, bottom: 0.25, left: 0.25, right: 0.25 },
            micrPosition: { fromBottom: 0.3125, fromLeft: 0.625 },
            fontFamily: 'Arial',
            colors: {
                primary: '#0C1F40',
                secondary: '#ED1C24',
                background: '#FFFFFF'
            },
            // Catalogue rebuild (fidelity REGIONAL wave 1, 2026-11-05):
            // Deluxe is Fifth Third's check partner (a, 53.com).
            // The evidenced original is the Deluxe catalogue design
            // done completely: safety pantograph ground ('security'
            // texture; the old 'multi_tone' was a decorative depth
            // pattern) and the bank imprint in Fifth Third navy.
            // Security set: Deluxe Standard (wave-1 correction).
            // No bank-specific safety tint or furniture is
            // evidenced → no-evidence-standard (ledger). The
            // previous build is preserved as FIFTH_THIRD_STUDIO.
            securityFeatures: ['chemical-sensitive-paper', 'microprint-border', 'security-screentone', 'erasure-protection']
        }
    },

    KEYBANK: {
        name: 'KeyBank Business',
        specs: {
            dimensions: { width: 3.5, height: 8.5, unit: 'inches' },
            margins: { top: 0.25, bottom: 0.25, left: 0.25, right: 0.25 },
            micrPosition: { fromBottom: 0.3125, fromLeft: 0.625 },
            fontFamily: 'Verdana',
            colors: {
                primary: '#CC0000',
                secondary: '#1a1a1a',
                background: '#FFFFFF'
            },
            // Catalogue rebuild (fidelity REGIONAL wave 1, 2026-11-05):
            // Harland Clarke is KeyBank's preferred check provider
            // (a, key.com). Business template: the evidenced
            // original is the HC Business catalogue design done
            // completely — Security Weave safety ground ('security'
            // texture; the old 'circuit_board' was a fintech
            // pattern no check carries) and the bank imprint in
            // KeyBank's published identity: the key symbol in red
            // #CC0000 and the wordmark in black (b, logo references
            // agree on black wordmark + red key) — the old build's
            // blue primary / green secondary belonged to other
            // banks' palettes entirely. Security set: HC Business
            // Standard (wave-1 correction). No bank-specific
            // safety tint or furniture is evidenced →
            // no-evidence-standard (ledger). The previous build is
            // preserved as KEYBANK_STUDIO.
            securityFeatures: ['security-weave', 'microprinting', 'security-screentone', 'fugitive-ink', 'chemical-reactive-paper', 'uv-dull-paper']
        }
    },

    CITIZENS: {
        name: 'Citizens Bank',
        specs: {
            dimensions: { width: 3.5, height: 8.5, unit: 'inches' },
            margins: { top: 0.25, bottom: 0.25, left: 0.25, right: 0.25 },
            micrPosition: { fromBottom: 0.3125, fromLeft: 0.625 },
            fontFamily: 'Arial',
            colors: {
                primary: '#00965E',
                secondary: '#FFFFFF',
                background: '#FFFFFF'
            },
            // REGIONAL WAVE 2 REBUILD (2026-11-05, owner standard —
            // "rebuild them to their original bank designs"). Printer
            // (a): Deluxe — Citizens' own pages name Deluxe "our
            // preferred check provider" (citizensbank.com business
            // checking overview; HELOC FAQ routes checkbook orders
            // to Deluxe). Security set: Deluxe Standard. The old
            // list claimed a holographic foil — an HS-only feature,
            // never standard. Colourway: the published identity is a
            // green sans wordmark (b); the stated brand green
            // #00965E replaces the darker unevidenced #006341. No
            // bank-specific safety tint is evidenced → white paper,
            // no-evidence-standard (ledger). The previous build is
            // preserved as CITIZENS_STUDIO.
            securityFeatures: ['chemical-sensitive-paper', 'microprint-border', 'security-screentone', 'erasure-protection']
        }
    },

    ALLY: {
        name: 'Ally Bank Digital',
        specs: {
            dimensions: { width: 3.5, height: 8.5, unit: 'inches' },
            margins: { top: 0.25, bottom: 0.25, left: 0.25, right: 0.25 },
            micrPosition: { fromBottom: 0.3125, fromLeft: 0.625 },
            fontFamily: 'Roboto',
            colors: {
                primary: '#643090',
                secondary: '#00A19A',
                background: '#FFFFFF'
            },
            // DIGITAL wave (2026-11-05): printer NOT FOUND (c) —
            // Ally's own pages confirm standard checks are free on
            // Spending/Money Market accounts but name no printer, and
            // its deposit agreement names none either. The set is
            // therefore the programme DEFAULT (Deluxe Standard, the
            // COMERICA/PENFED precedent) — a default, NOT evidence,
            // flagged in the ledger. The old list claimed digital
            // schemes ('digital-signature', 'qr-verification') on a
            // paper check; they are verification claims, not paper
            // features, and leave with the correction; 'microprinting'
            // is in the Deluxe list in mapped form ('microprint-
            // border'). Colourway: the published wordmark is ONE plum
            // ink (b; logo-file measurement #3E0E45 — the same deep-
            // plum family, shade-level, so the primary stands, the
            // NFCU precedent). The teal secondary has no published
            // counterpart → retained, no-evidence-standard. Roboto
            // stands — the wordmark itself is custom artwork; no churn
            // (BECU precedent). The previous build is preserved as
            // ALLY_STUDIO.
            securityFeatures: ['chemical-sensitive-paper', 'microprint-border', 'security-screentone', 'erasure-protection']
        }
    },

    ALLY_STUDIO: {
        name: 'Ally Bank Digital (Studio Variant)',
        specs: {
            dimensions: { width: 3.5, height: 8.5, unit: 'inches' },
            margins: { top: 0.25, bottom: 0.25, left: 0.25, right: 0.25 },
            micrPosition: { fromBottom: 0.3125, fromLeft: 0.625 },
            fontFamily: 'Roboto',
            colors: {
                primary: '#643090',
                secondary: '#00A19A',
                background: '#FFFFFF'
            },
            securityFeatures: ['chemical-sensitive-paper', 'microprint-border', 'security-screentone', 'erasure-protection']
        }
    },

    DISCOVER: {
        name: 'Discover Bank',
        specs: {
            dimensions: { width: 3.5, height: 8.5, unit: 'inches' },
            margins: { top: 0.25, bottom: 0.25, left: 0.25, right: 0.25 },
            micrPosition: { fromBottom: 0.3125, fromLeft: 0.625 },
            fontFamily: 'Arial',
            colors: {
                primary: '#FF6600',
                secondary: '#003A5D',
                background: '#FFFFFF'
            },
            // DIGITAL wave (2026-11-05): printer NOT FOUND (c) —
            // Cashback Debit provides free check reorders, but no
            // Discover source names a printer. (Discover was acquired
            // by Capital One, completed 2025-05-18 (b); the acquirer's
            // printer is Harland Clarke, but that is the ACQUIRER's
            // printer — not evidence for Discover-branded checks.)
            // The set is therefore the programme DEFAULT (Deluxe
            // Standard, COMERICA/PENFED precedent) — flagged in the
            // ledger, not evidence. The old list claimed
            // 'color-shifting-ink' — an optically-variable ink
            // technology, not in Deluxe's standard named list — and
            // 'security-thread', a construction, not a printed
            // feature; both leave with the correction. Identity (b):
            // since 2001 the published mark is BLACK letters with only
            // the central "O" carrying the orange sun — so the primary
            // #FF6600 (the published Discover Orange) stands as the
            // mark's ink, the NAME ink splits to near-black via
            // TEMPLATE_WORDMARK_INK, and the weave takes the neutral
            // override via TEMPLATE_WEAVE_INK (the GOLDEN1
            // discriminating case in orange — an orange weave would
            // paint the whole face orange; no orange safety paper is
            // evidenced). The navy secondary has no published
            // counterpart → retained, no-evidence-standard. The
            // previous build is preserved as DISCOVER_STUDIO.
            securityFeatures: ['chemical-sensitive-paper', 'microprint-border', 'security-screentone', 'erasure-protection']
        }
    },

    DISCOVER_STUDIO: {
        name: 'Discover Bank (Studio Variant)',
        specs: {
            dimensions: { width: 3.5, height: 8.5, unit: 'inches' },
            margins: { top: 0.25, bottom: 0.25, left: 0.25, right: 0.25 },
            micrPosition: { fromBottom: 0.3125, fromLeft: 0.625 },
            fontFamily: 'Arial',
            colors: {
                primary: '#FF6600',
                secondary: '#003A5D',
                background: '#FFFFFF'
            },
            securityFeatures: ['chemical-sensitive-paper', 'microprint-border', 'security-screentone', 'erasure-protection']
        }
    },

    // ============== REGIONAL BANKS ==============

    REGIONS: {
        name: 'Regions Bank',
        specs: {
            dimensions: { width: 3.5, height: 8.5, unit: 'inches' },
            margins: { top: 0.25, bottom: 0.25, left: 0.25, right: 0.25 },
            micrPosition: { fromBottom: 0.3125, fromLeft: 0.625 },
            fontFamily: 'Arial',
            colors: {
                primary: '#78BE20',
                secondary: '#000000',
                background: '#FFFFFF'
            },
            // Catalogue rebuild (fidelity REGIONAL wave 1, 2026-11-05):
            // Harland Clarke is Regions' check printer (a, Regions'
            // own order page). The evidenced original is the HC
            // catalogue design done completely: the Security Weave
            // safety ground (the 'security' texture — HC's named
            // woven ground (a, Vericast comparison sheet)) and the
            // bank imprint in Regions' official LifeGreen (a,
            // regions.com logo-and-brand: "Regions' official color
            // is ... LifeGreen"; the logo is a sans wordmark with
            // the pyramid mark, so the imprint face is the standard
            // sans — the old Garamond serif contradicted the bank's
            // published identity). Security set: HC Standard
            // Personal (wave-1 correction). No bank-specific safety
            // tint or furniture is evidenced → standard composition
            // (no-evidence-standard, ledger). The previous build is
            // preserved as REGIONS_STUDIO.
            securityFeatures: ['security-weave', 'microprinting', 'security-screentone', 'fugitive-ink']
        }
    },

    TRUIST: {
        name: 'Truist (SunTrust/BB&T)',
        specs: {
            dimensions: { width: 3.5, height: 8.5, unit: 'inches' },
            margins: { top: 0.25, bottom: 0.25, left: 0.25, right: 0.25 },
            micrPosition: { fromBottom: 0.3125, fromLeft: 0.625 },
            fontFamily: 'Lato',
            colors: {
                primary: '#2E1A47',
                secondary: '#CE0058',
                background: '#FFFFFF'
            },
            // Catalogue rebuild (fidelity REGIONAL wave 1, 2026-11-05):
            // Deluxe is Truist's check printer (b, Truist help flow).
            // The evidenced original is the Deluxe catalogue design
            // done completely: the safety pantograph ground (the
            // 'security' texture) and the bank imprint in Truist
            // Purple (b, the bank's published brand colour) with a
            // humanist-sans imprint face. Security set: Deluxe
            // Standard (wave-1 correction). No Truist-specific
            // safety tint or furniture is evidenced →
            // no-evidence-standard (ledger). The previous build is
            // preserved as TRUIST_STUDIO.
            securityFeatures: ['chemical-sensitive-paper', 'microprint-border', 'security-screentone', 'erasure-protection']
        }
    },

    BMO: {
        name: 'BMO Harris',
        specs: {
            dimensions: { width: 3.5, height: 8.5, unit: 'inches' },
            margins: { top: 0.25, bottom: 0.25, left: 0.25, right: 0.25 },
            micrPosition: { fromBottom: 0.3125, fromLeft: 0.625 },
            fontFamily: 'Arial',
            colors: {
                primary: '#0079C1',
                secondary: '#D52B1E',
                background: '#FFFFFF'
            },
            // Catalogue rebuild (fidelity REGIONAL wave 1, 2026-11-05):
            // Deluxe is BMO's check printer (a, BMO's own fee
            // schedule: "Deluxe(R) Check Orders"). The evidenced
            // original is the Deluxe catalogue design done
            // completely: safety pantograph ground ('security'
            // texture — this build already carried it) and the BMO
            // blue imprint. Security set: Deluxe Standard (wave-1
            // correction, which also removed the unevidenced
            // 'watermark-lion'). No BMO-specific safety tint or
            // furniture is evidenced → no-evidence-standard
            // (ledger). NO Studio Variant for BMO: the rebuild
            // overwrote no BMO design token (its build already
            // carried the safety ground and palette), so a preserved
            // entry would be an exact duplicate of the main — the
            // catalogue forbids collapsed designs (designPresets
            // distinctness pin).
            securityFeatures: ['chemical-sensitive-paper', 'microprint-border', 'security-screentone', 'erasure-protection']
        }
    },

    // — REGIONAL wave-1 Studio Variants (2026-11-05): the banks'
    // pre-rebuild builds, preserved verbatim (design tokens, colours,
    // fonts) under the owner's rebuild standard, exactly as
    // GREENFI_MINT and ANB_SKY preserved the specimen builds. Their
    // security sets are the live wave-1-corrected sets (the
    // correction is a product fact, not a design choice). Backs
    // resolve through the same V10 fallback as the mains. BMO has no
    // variant — see the note on the BMO entry.
    REGIONS_STUDIO: {
        name: 'Regions Bank (Studio Variant)',
        specs: {
            dimensions: { width: 3.5, height: 8.5, unit: 'inches' },
            margins: { top: 0.25, bottom: 0.25, left: 0.25, right: 0.25 },
            micrPosition: { fromBottom: 0.3125, fromLeft: 0.625 },
            fontFamily: 'Garamond',
            colors: {
                primary: '#78BE20',
                secondary: '#000000',
                background: '#FFFFFF'
            },
            securityFeatures: ['security-weave', 'microprinting', 'security-screentone', 'fugitive-ink']
        }
    },
    TRUIST_STUDIO: {
        name: 'Truist (Studio Variant)',
        specs: {
            dimensions: { width: 3.5, height: 8.5, unit: 'inches' },
            margins: { top: 0.25, bottom: 0.25, left: 0.25, right: 0.25 },
            micrPosition: { fromBottom: 0.3125, fromLeft: 0.625 },
            fontFamily: 'Lato',
            colors: {
                primary: '#2E1A47',
                secondary: '#CE0058',
                background: '#FFFFFF'
            },
            securityFeatures: ['chemical-sensitive-paper', 'microprint-border', 'security-screentone', 'erasure-protection']
        }
    },
    HUNTINGTON_STUDIO: {
        name: 'Huntington Business (Studio Variant)',
        specs: {
            dimensions: { width: 3.5, height: 8.5, unit: 'inches' },
            margins: { top: 0.25, bottom: 0.25, left: 0.25, right: 0.25 },
            micrPosition: { fromBottom: 0.3125, fromLeft: 0.625 },
            fontFamily: 'Georgia',
            colors: {
                primary: '#006747',
                secondary: '#A5A5A5',
                background: '#FFFFFF'
            },
            securityFeatures: ['security-weave', 'microprinting', 'security-screentone', 'fugitive-ink', 'chemical-reactive-paper', 'uv-dull-paper']
        }
    },
    FIFTH_THIRD_STUDIO: {
        name: 'Fifth Third Bank (Studio Variant)',
        specs: {
            dimensions: { width: 3.5, height: 8.5, unit: 'inches' },
            margins: { top: 0.25, bottom: 0.25, left: 0.25, right: 0.25 },
            micrPosition: { fromBottom: 0.3125, fromLeft: 0.625 },
            fontFamily: 'Arial',
            colors: {
                primary: '#0C1F40',
                secondary: '#ED1C24',
                background: '#FFFFFF'
            },
            securityFeatures: ['chemical-sensitive-paper', 'microprint-border', 'security-screentone', 'erasure-protection']
        }
    },
    KEYBANK_STUDIO: {
        name: 'KeyBank Business (Studio Variant)',
        specs: {
            dimensions: { width: 3.5, height: 8.5, unit: 'inches' },
            margins: { top: 0.25, bottom: 0.25, left: 0.25, right: 0.25 },
            micrPosition: { fromBottom: 0.3125, fromLeft: 0.625 },
            fontFamily: 'Verdana',
            colors: {
                primary: '#005DAA',
                secondary: '#78BE14',
                background: '#FFFFFF'
            },
            securityFeatures: ['security-weave', 'microprinting', 'security-screentone', 'fugitive-ink', 'chemical-reactive-paper', 'uv-dull-paper']
        }
    },
    // REGIONAL WAVE 2 — Studio Variants (2026-11-05): the five
    // banks' pre-rebuild builds, preserved before the evidenced
    // catalogue rebuilds took the bank names (the GREENFI_MINT /
    // ANB_SKY / wave-1 variant pattern). Design tokens + palette +
    // imprint face are the pre-rebuild values verbatim; the security
    // sets are the live wave-2-corrected sets (the correction is a
    // product fact, not a design choice). Backs resolve through the
    // same V10 fallback as the mains.
    CITIZENS_STUDIO: {
        name: 'Citizens Bank (Studio Variant)',
        specs: {
            dimensions: { width: 3.5, height: 8.5, unit: 'inches' },
            margins: { top: 0.25, bottom: 0.25, left: 0.25, right: 0.25 },
            micrPosition: { fromBottom: 0.3125, fromLeft: 0.625 },
            fontFamily: 'Arial',
            colors: {
                primary: '#006341',
                secondary: '#FFFFFF',
                background: '#FFFFFF'
            },
            securityFeatures: ['chemical-sensitive-paper', 'microprint-border', 'security-screentone', 'erasure-protection']
        }
    },
    ZIONS_STUDIO: {
        name: 'Zions Bank (Studio Variant)',
        specs: {
            dimensions: { width: 3.5, height: 8.5, unit: 'inches' },
            margins: { top: 0.25, bottom: 0.25, left: 0.25, right: 0.25 },
            micrPosition: { fromBottom: 0.3125, fromLeft: 0.625 },
            fontFamily: 'Tahoma',
            colors: {
                primary: '#003366',
                secondary: '#CC0000',
                background: '#FFFFFF'
            },
            securityFeatures: ['chemical-sensitive-paper', 'microprint-border', 'security-screentone', 'erasure-protection']
        }
    },
    COMERICA_STUDIO: {
        name: 'Comerica Bank (Studio Variant)',
        specs: {
            dimensions: { width: 3.5, height: 8.5, unit: 'inches' },
            margins: { top: 0.25, bottom: 0.25, left: 0.25, right: 0.25 },
            micrPosition: { fromBottom: 0.3125, fromLeft: 0.625 },
            fontFamily: 'Arial',
            colors: {
                primary: '#007749',
                secondary: '#000000',
                background: '#FFFFFF'
            },
            securityFeatures: ['chemical-sensitive-paper', 'microprint-border', 'security-screentone', 'erasure-protection']
        }
    },
    FIRST_HORIZON_STUDIO: {
        name: 'First Horizon (Studio Variant)',
        specs: {
            dimensions: { width: 3.5, height: 8.5, unit: 'inches' },
            margins: { top: 0.25, bottom: 0.25, left: 0.25, right: 0.25 },
            micrPosition: { fromBottom: 0.3125, fromLeft: 0.625 },
            fontFamily: 'Georgia',
            colors: {
                primary: '#003C71',
                secondary: '#C8102E',
                background: '#FFFFFF'
            },
            securityFeatures: ['security-weave', 'microprinting', 'security-screentone', 'fugitive-ink']
        }
    },
    M_AND_T_STUDIO: {
        name: 'M&T Bank (Studio Variant)',
        specs: {
            dimensions: { width: 3.5, height: 8.5, unit: 'inches' },
            margins: { top: 0.25, bottom: 0.25, left: 0.25, right: 0.25 },
            micrPosition: { fromBottom: 0.3125, fromLeft: 0.625 },
            fontFamily: 'Times New Roman',
            colors: {
                primary: '#EE3124',
                secondary: '#231F20',
                background: '#FFFFFF'
            },
            securityFeatures: ['chemical-sensitive-paper', 'microprint-border', 'security-screentone', 'erasure-protection']
        }
    },
    // MAJOR REBUILD WAVE — Studio Variants (2026-11-05): the nine
    // majors' pre-rebuild builds, preserved before the evidenced
    // catalogue rebuilds took the bank names (the GREENFI_MINT /
    // ANB_SKY / regional-variant pattern). Design tokens + palette +
    // imprint face are the pre-rebuild values verbatim; the security
    // sets are the live batch-1-corrected sets (the correction is a
    // product fact, not a design choice). Backs resolve through the
    // same fallback as the mains (BOFA_STUDIO keeps BOFA's V9 via
    // BANK_BACK). CHASE and TD have no variant — their rebuilds
    // changed no effective-design field, so a variant would be
    // byte-identical to the main (the BMO precedent).
    BOFA_STUDIO: {
        name: 'Bank of America Premium Business (Studio Variant)',
        specs: {
            dimensions: { width: 3.5, height: 8.5, unit: 'inches' },
            margins: { top: 0.25, bottom: 0.25, left: 0.25, right: 0.25 },
            micrPosition: { fromBottom: 0.25, fromLeft: 0.625 },
            fontFamily: 'Helvetica Neue',
            colors: {
                primary: '#012169',
                secondary: '#D52B1E',
                background: '#FFFFFF'
            },
            securityFeatures: ['security-weave', 'microprinting', 'security-screentone', 'fugitive-ink']
        },
        fields: [
            { name: 'payee', x: 0.75, y: 2.0, width: 4.0 },
            { name: 'amount_numbers', x: 5.5, y: 2.0, width: 1.5 },
            { name: 'amount_words', x: 0.75, y: 2.5, width: 6.25 },
            { name: 'date', x: 5.5, y: 0.75, width: 1.5 },
            { name: 'check_number', x: 6.75, y: 0.75, width: 0.75 },
            { name: 'memo', x: 0.75, y: 3.0, width: 2.5 },
            { name: 'signature', x: 4.5, y: 3.25, width: 2.5 },
            { name: 'bank_name', x: 0.75, y: 0.5, width: 3.0, fontSize: 14, bold: true },
            { name: 'bank_address', x: 0.75, y: 0.75, width: 3.0, fontSize: 9 },
            { name: 'account_holder', x: 0.75, y: 1.25, width: 3.0, fontSize: 11 },
            { name: 'account_address', x: 0.75, y: 1.5, width: 3.0, fontSize: 9 }
        ]
    },
    CHASE_PRIVATE_STUDIO: {
        name: 'Chase Private Client (Studio Variant)',
        specs: {
            dimensions: { width: 3.5, height: 8.5, unit: 'inches' },
            margins: { top: 0.2, bottom: 0.2, left: 0.2, right: 0.2 },
            micrPosition: { fromBottom: 0.3125, fromLeft: 0.625 },
            fontFamily: 'Gotham',
            colors: {
                primary: '#117ACA',
                secondary: '#B8860B',
                accent: '#F5F5F5',
                background: '#FFFFFF'
            },
            securityFeatures: ['chemical-sensitive-paper', 'microprint-border', 'security-screentone', 'erasure-protection']
        }
    },
    WELLS_STUDIO: {
        name: 'Wells Fargo Executive (Studio Variant)',
        specs: {
            dimensions: { width: 3.5, height: 8.5, unit: 'inches' },
            margins: { top: 0.25, bottom: 0.25, left: 0.25, right: 0.25 },
            micrPosition: { fromBottom: 0.375, fromLeft: 0.75 },
            fontFamily: 'Whitney',
            colors: {
                primary: '#F26D21',
                secondary: '#231F20',
                background: '#FFFFFF'
            },
            securityFeatures: ['security-weave', 'microprinting', 'security-screentone', 'fugitive-ink']
        }
    },
    CITI_STUDIO: {
        name: 'CitiBusiness Professional (Studio Variant)',
        specs: {
            dimensions: { width: 3.5, height: 8.5, unit: 'inches' },
            margins: { top: 0.25, bottom: 0.25, left: 0.25, right: 0.25 },
            micrPosition: { fromBottom: 0.3125, fromLeft: 0.625 },
            fontFamily: 'Futura',
            colors: {
                primary: '#003B70',
                secondary: '#EF3E42',
                accent: '#00B3B0',
                background: '#FFFFFF'
            },
            securityFeatures: ['chemical-sensitive-paper', 'microprint-border', 'security-screentone', 'erasure-protection']
        }
    },
    PNC_STUDIO: {
        name: 'PNC Business Elite (Studio Variant)',
        specs: {
            dimensions: { width: 3.5, height: 8.5, unit: 'inches' },
            margins: { top: 0.25, bottom: 0.25, left: 0.25, right: 0.25 },
            micrPosition: { fromBottom: 0.3125, fromLeft: 0.625 },
            fontFamily: 'Helvetica',
            colors: {
                primary: '#FF6600',
                secondary: '#1D242D',
                accent: '#FFFFFF',
                background: '#FFFFFF'
            },
            securityFeatures: ['security-weave', 'microprinting', 'security-screentone', 'fugitive-ink']
        }
    },
    CAPITAL_ONE_STUDIO: {
        name: 'Capital One Premium (Studio Variant)',
        specs: {
            dimensions: { width: 3.5, height: 8.5, unit: 'inches' },
            margins: { top: 0.25, bottom: 0.25, left: 0.25, right: 0.25 },
            micrPosition: { fromBottom: 0.25, fromLeft: 0.625 },
            fontFamily: 'Proxima Nova',
            colors: {
                primary: '#D03027',
                secondary: '#004879',
                accent: '#FFFFFF',
                background: '#FFFFFF'
            },
            securityFeatures: ['security-weave', 'microprinting', 'security-screentone', 'fugitive-ink']
        }
    },
    US_BANK_STUDIO: {
        name: 'US Bank Commercial (Studio Variant)',
        specs: {
            dimensions: { width: 3.5, height: 8.5, unit: 'inches' },
            margins: { top: 0.25, bottom: 0.25, left: 0.25, right: 0.25 },
            micrPosition: { fromBottom: 0.3125, fromLeft: 0.625 },
            fontFamily: 'Avenir',
            colors: {
                primary: '#0C2340',
                secondary: '#D71920',
                background: '#FFFFFF'
            },
            securityFeatures: ['chemical-sensitive-paper', 'microprint-border', 'security-screentone', 'erasure-protection']
        }
    },
    ZIONS: {
        name: 'Zions Bank',
        specs: {
            dimensions: { width: 3.5, height: 8.5, unit: 'inches' },
            margins: { top: 0.25, bottom: 0.25, left: 0.25, right: 0.25 },
            micrPosition: { fromBottom: 0.3125, fromLeft: 0.625 },
            fontFamily: 'Tahoma',
            colors: {
                primary: '#003366',
                secondary: '#CC0000',
                background: '#FFFFFF'
            },
            // REGIONAL WAVE 2 REBUILD (2026-11-05). Printer (a):
            // Deluxe — Zions' own check-reorders page routes orders
            // to "our preferred check provider Deluxe"
            // (zionsbank.com). Security set: Deluxe Standard. The
            // old list's claims (spec-name void pantograph, security
            // fibres) are not the standard tier's named set.
            // Colourway stands: the published wordmark is navy (b)
            // and #003366 is its uncontradicted studio value; the
            // red secondary has no published counterpart and is
            // recorded no-evidence-standard in the ledger. The
            // previous build is preserved as ZIONS_STUDIO.
            securityFeatures: ['chemical-sensitive-paper', 'microprint-border', 'security-screentone', 'erasure-protection']
        }
    },

    COMERICA: {
        name: 'Comerica Bank',
        specs: {
            dimensions: { width: 3.5, height: 8.5, unit: 'inches' },
            margins: { top: 0.25, bottom: 0.25, left: 0.25, right: 0.25 },
            micrPosition: { fromBottom: 0.3125, fromLeft: 0.625 },
            fontFamily: 'Arial',
            colors: {
                primary: '#002F6C',
                secondary: '#000000',
                background: '#FFFFFF'
            },
            // REGIONAL WAVE 2 REBUILD (2026-11-05). Printer (c)
            // NOT FOUND: comerica.com and its fee disclosures name
            // no check printer in the sources searched, so the set
            // is the programme's default Deluxe Standard, flagged
            // in the ledger for correction if the printer surfaces.
            // The old list claimed a holographic strip — HS-only —
            // and the old ground 'toner_adhesion' was an HS feature
            // name used as a texture. Colourway corrected to the
            // published identity (b): the wordmark is navy #002F6C
            // (PMS 294C) — the old green #007749 belonged to other
            // banks. The previous build is preserved as
            // COMERICA_STUDIO.
            securityFeatures: ['chemical-sensitive-paper', 'microprint-border', 'security-screentone', 'erasure-protection']
        }
    },

    FIRST_HORIZON: {
        name: 'First Horizon',
        specs: {
            dimensions: { width: 3.5, height: 8.5, unit: 'inches' },
            margins: { top: 0.25, bottom: 0.25, left: 0.25, right: 0.25 },
            micrPosition: { fromBottom: 0.3125, fromLeft: 0.625 },
            fontFamily: 'Arial',
            colors: {
                primary: '#003C71',
                secondary: '#C8102E',
                background: '#FFFFFF'
            },
            // REGIONAL WAVE 2 REBUILD (2026-11-05). Printer (a):
            // Harland Clarke — First Horizon's own IBERIABANK
            // welcome guide routes check reorders to Harland Clarke
            // (firsthorizon.com). Plain-named template → HC
            // Standard Personal, the REGIONS precedent. The old
            // list claimed 'uv-reactive' fluorescent ink; the HC
            // standard carries UV DULL paper — the opposite claim —
            // and only at the business tier. Identity (b, incl.
            // First Horizon's own logo standards): a navy SANS
            // wordmark with a red accent — the colourway roles
            // stand (#003C71 / #C8102E, exact hexes unverified
            // (c)), the Georgia serif becomes the standard sans.
            // No chemical paper at this tier → the back carries no
            // wash box. The previous build is preserved as
            // FIRST_HORIZON_STUDIO.
            securityFeatures: ['security-weave', 'microprinting', 'security-screentone', 'fugitive-ink']
        }
    },

    M_AND_T: {
        name: 'M&T Bank',
        specs: {
            dimensions: { width: 3.5, height: 8.5, unit: 'inches' },
            margins: { top: 0.25, bottom: 0.25, left: 0.25, right: 0.25 },
            micrPosition: { fromBottom: 0.3125, fromLeft: 0.625 },
            fontFamily: 'Times New Roman',
            colors: {
                primary: '#0A8667',
                secondary: '#231F20',
                background: '#FFFFFF'
            },
            // REGIONAL WAVE 2 REBUILD (2026-11-05). Printer (b):
            // Deluxe — secondary sources consistently describe
            // M&T's reorder flow as ordering through its check
            // printer, Deluxe Corporation. Security set: Deluxe
            // Standard. Colourway corrected to the published
            // identity (b): a forest/emerald GREEN wordmark —
            // #0A8667 measured from the logo file — replacing the
            // red #EE3124, which no published source associates
            // with M&T. The bold condensed serif of the published
            // "M&T" keeps the Times imprint face. The previous
            // build is preserved as M_AND_T_STUDIO.
            securityFeatures: ['chemical-sensitive-paper', 'microprint-border', 'security-screentone', 'erasure-protection']
        }
    },

    // ============== CREDIT UNIONS ==============

    NAVY_FCU: {
        name: 'Navy Federal Credit Union',
        specs: {
            dimensions: { width: 3.5, height: 8.5, unit: 'inches' },
            margins: { top: 0.25, bottom: 0.25, left: 0.25, right: 0.25 },
            micrPosition: { fromBottom: 0.3125, fromLeft: 0.625 },
            fontFamily: 'Arial',
            colors: {
                primary: '#003B5C',
                secondary: '#B31B1B',
                accent: '#C4A000',
                background: '#FFFFFF'
            },
            // CREDIT UNION WAVE REBUILD (2026-11-05, owner standard —
            // "rebuild them to their original bank designs"). Printer
            // (a): Harland Clarke — Navy Federal's own business
            // partners page (navyfederal.org): "Harland Clarke offers
            // business checks, forms and accessories… order online"
            // via ordermychecks.com, HC's own channel. Security set:
            // HC Standard Personal. The old list claimed a
            // holographic eagle (a foil hologram is HC HIGH-SECURITY
            // only, and the eagle is artwork besides), UV fibres (a
            // fluorescent claim the standard tier does not carry) and
            // a security thread (a construction, not a printed
            // feature). Colourway: the published identity is a
            // ONE-INK navy wordmark (b — the logo file is a single
            // navy throughout; measured #003767); primary #003B5C
            // stands in the same deep-navy family (shade-level
            // difference is not a contradiction). The red secondary
            // and gold accent have no published counterpart → retained
            // and recorded no-evidence-standard (ledger). The previous
            // build is preserved as NAVY_FCU_STUDIO.
            securityFeatures: ['security-weave', 'microprinting', 'security-screentone', 'fugitive-ink']
        }
    },

    PENFED: {
        name: 'Pentagon FCU',
        specs: {
            dimensions: { width: 3.5, height: 8.5, unit: 'inches' },
            margins: { top: 0.25, bottom: 0.25, left: 0.25, right: 0.25 },
            micrPosition: { fromBottom: 0.3125, fromLeft: 0.625 },
            fontFamily: 'Arial',
            colors: {
                primary: '#003366',
                secondary: '#B8860B',
                background: '#FFFFFF'
            },
            // CREDIT UNION WAVE REBUILD (2026-11-05). Printer (c):
            // NOT FOUND — PenFed's own help page (home.penfed.org)
            // names only "our third party provider", and repeated
            // targeted searches surfaced no printer. The set is
            // therefore the programme DEFAULT (Deluxe Standard, the
            // COMERICA precedent) — recorded here as a default, NOT
            // as evidence, and flagged in the ledger for correction
            // if the printer surfaces. The old list was a fragment
            // (a construction thread + two features). Colourway (b):
            // the published identity is a navy wordmark with a gold
            // accent; primary #003366 / secondary #B8860B stand in
            // those families (exact hexes unverified (c)). The
            // previous build is preserved as PENFED_STUDIO.
            securityFeatures: ['chemical-sensitive-paper', 'microprint-border', 'security-screentone', 'erasure-protection']
        }
    },

    BECU: {
        name: 'BECU',
        specs: {
            dimensions: { width: 3.5, height: 8.5, unit: 'inches' },
            margins: { top: 0.25, bottom: 0.25, left: 0.25, right: 0.25 },
            micrPosition: { fromBottom: 0.3125, fromLeft: 0.625 },
            fontFamily: 'Verdana',
            colors: {
                primary: '#00529B',
                secondary: '#6CC24A',
                background: '#FFFFFF'
            },
            // CREDIT UNION WAVE REBUILD (2026-11-05). Printer (a):
            // Deluxe — BECU's own order-checks page (becu.org):
            // "You can view available designs by visiting Deluxe…
            // prices are set by Deluxe… you can reorder directly
            // from Deluxe." Security set: Deluxe Standard. The old
            // list claimed 'uv-reactive' — a fluorescent claim that
            // is not in Deluxe's standard named list. Colourway (b):
            // the published wordmark is blue (2017 rebrand: the
            // BECU letters in a rounded-corner box, becu.org/logo);
            // primary #00529B stands. The green secondary has no
            // published counterpart → retained and recorded
            // no-evidence-standard (ledger). Verdana stands (the
            // imprint face is not evidenced either way → no churn).
            // The previous build is preserved as BECU_STUDIO.
            securityFeatures: ['chemical-sensitive-paper', 'microprint-border', 'security-screentone', 'erasure-protection']
        }
    },

    ALLIANT: {
        name: 'Alliant Credit Union',
        specs: {
            dimensions: { width: 3.5, height: 8.5, unit: 'inches' },
            margins: { top: 0.25, bottom: 0.25, left: 0.25, right: 0.25 },
            micrPosition: { fromBottom: 0.3125, fromLeft: 0.625 },
            fontFamily: 'Arial',
            colors: {
                primary: '#004B87',
                secondary: '#00A651',
                background: '#FFFFFF'
            },
            // CREDIT UNION WAVE REBUILD (2026-11-05). Printer (c):
            // NOT FOUND — Alliant's own help pages route check
            // ordering to an unnamed third-party site, and repeated
            // targeted searches surfaced no printer. The set is
            // therefore the programme DEFAULT (Deluxe Standard, the
            // COMERICA precedent) — recorded here as a default, NOT
            // as evidence, and flagged in the ledger for correction
            // if the printer surfaces. The old list was a fragment
            // (two features + a paper property). Colourway (b): the
            // published identity is a navy wordmark; primary #004B87
            // stands in that family (exact hex unverified (c)). The
            // green secondary has no published counterpart beyond
            // the mark's blue-green arc → retained and recorded
            // no-evidence-standard (ledger). The previous build is
            // preserved as ALLIANT_STUDIO.
            securityFeatures: ['chemical-sensitive-paper', 'microprint-border', 'security-screentone', 'erasure-protection']
        }
    },

    GOLDEN1: {
        name: 'Golden 1 Credit Union',
        specs: {
            dimensions: { width: 3.5, height: 8.5, unit: 'inches' },
            margins: { top: 0.25, bottom: 0.25, left: 0.25, right: 0.25 },
            micrPosition: { fromBottom: 0.3125, fromLeft: 0.625 },
            fontFamily: 'Arial',
            colors: {
                primary: '#FFB81C',
                secondary: '#000000',
                background: '#FFFFFF'
            },
            // CREDIT UNION WAVE REBUILD (2026-11-05). Printer (c):
            // NOT FOUND — Golden 1's own help routes business check
            // orders through member support and names no printer;
            // repeated targeted searches surfaced none. The set is
            // therefore the programme DEFAULT (Deluxe Standard, the
            // COMERICA precedent) — recorded here as a default, NOT
            // as evidence, and flagged in the ledger for correction
            // if the printer surfaces. The old list was a fragment.
            // Colourway (b): the published wordmark's LETTERS ARE
            // GOLD (the logo file's fill measures #F7D02D, the brand
            // gold family) with the lockup's sub-line in BLACK
            // (measured #000000) — so the name ink IS the gold
            // primary (no TEMPLATE_WORDMARK_INK split; the KeyBank
            // case does not apply), primary #FFB81C stands as the
            // stated brand gold, and the secondary corrects from
            // the unevidenced navy #003366 to the measured black.
            // The gold weave ink would paint the whole face yellow,
            // so the face takes the neutral weave override (see
            // TEMPLATE_WEAVE_INK). The previous build is preserved
            // as GOLDEN1_STUDIO (with its navy secondary verbatim).
            securityFeatures: ['chemical-sensitive-paper', 'microprint-border', 'security-screentone', 'erasure-protection']
        }
    },

    // CREDIT UNION WAVE — Studio Variants (2026-11-05): the five
    // credit unions' pre-rebuild builds, preserved before the
    // evidenced catalogue rebuilds took the names (the GREENFI_MINT /
    // ANB_SKY / regional-variant pattern). Design tokens + palette +
    // imprint face are the pre-rebuild values verbatim; the security
    // sets are the live wave-corrected sets (the correction is a
    // product fact, not a design choice). Backs resolve through the
    // same V10 fallback as the mains.
    NAVY_FCU_STUDIO: {
        name: 'Navy Federal Credit Union (Studio Variant)',
        specs: {
            dimensions: { width: 3.5, height: 8.5, unit: 'inches' },
            margins: { top: 0.25, bottom: 0.25, left: 0.25, right: 0.25 },
            micrPosition: { fromBottom: 0.3125, fromLeft: 0.625 },
            fontFamily: 'Arial',
            colors: {
                primary: '#003B5C',
                secondary: '#B31B1B',
                accent: '#C4A000',
                background: '#FFFFFF'
            },
            securityFeatures: ['security-weave', 'microprinting', 'security-screentone', 'fugitive-ink']
        }
    },

    PENFED_STUDIO: {
        name: 'Pentagon FCU (Studio Variant)',
        specs: {
            dimensions: { width: 3.5, height: 8.5, unit: 'inches' },
            margins: { top: 0.25, bottom: 0.25, left: 0.25, right: 0.25 },
            micrPosition: { fromBottom: 0.3125, fromLeft: 0.625 },
            fontFamily: 'Arial',
            colors: {
                primary: '#003366',
                secondary: '#B8860B',
                background: '#FFFFFF'
            },
            securityFeatures: ['chemical-sensitive-paper', 'microprint-border', 'security-screentone', 'erasure-protection']
        }
    },

    BECU_STUDIO: {
        name: 'BECU (Studio Variant)',
        specs: {
            dimensions: { width: 3.5, height: 8.5, unit: 'inches' },
            margins: { top: 0.25, bottom: 0.25, left: 0.25, right: 0.25 },
            micrPosition: { fromBottom: 0.3125, fromLeft: 0.625 },
            fontFamily: 'Verdana',
            colors: {
                primary: '#00529B',
                secondary: '#6CC24A',
                background: '#FFFFFF'
            },
            securityFeatures: ['chemical-sensitive-paper', 'microprint-border', 'security-screentone', 'erasure-protection']
        }
    },

    ALLIANT_STUDIO: {
        name: 'Alliant Credit Union (Studio Variant)',
        specs: {
            dimensions: { width: 3.5, height: 8.5, unit: 'inches' },
            margins: { top: 0.25, bottom: 0.25, left: 0.25, right: 0.25 },
            micrPosition: { fromBottom: 0.3125, fromLeft: 0.625 },
            fontFamily: 'Arial',
            colors: {
                primary: '#004B87',
                secondary: '#00A651',
                background: '#FFFFFF'
            },
            securityFeatures: ['chemical-sensitive-paper', 'microprint-border', 'security-screentone', 'erasure-protection']
        }
    },

    GOLDEN1_STUDIO: {
        name: 'Golden 1 Credit Union (Studio Variant)',
        specs: {
            dimensions: { width: 3.5, height: 8.5, unit: 'inches' },
            margins: { top: 0.25, bottom: 0.25, left: 0.25, right: 0.25 },
            micrPosition: { fromBottom: 0.3125, fromLeft: 0.625 },
            fontFamily: 'Arial',
            colors: {
                primary: '#FFB81C',
                secondary: '#003366',
                background: '#FFFFFF'
            },
            securityFeatures: ['chemical-sensitive-paper', 'microprint-border', 'security-screentone', 'erasure-protection']
        }
    },

    // ============== SPECIALTY CHECK TYPES ==============

    CASHIER: {
        name: "Cashier's Check (Certified)",
        specs: {
            dimensions: { width: 3.75, height: 8.75, unit: 'inches' },
            margins: { top: 0.5, bottom: 0.5, left: 0.5, right: 0.5 },
            micrPosition: { fromBottom: 0.3125, fromLeft: 0.625 },
            fontFamily: 'Georgia',
            colors: {
                primary: '#1A4D1A',
                secondary: '#FFD700',
                background: '#F0FFF0'
            },
            // SPECIALTY wave (2026-11-05): a cashier's check is a
            // HIGH-SECURITY instrument by definition — the set is the
            // Deluxe High Security NAMED list (a: Deluxe flashcard
            // via fcbanking.com + deluxe.com/highsecurity, recorded
            // in the ledger's CHASE research): foil hologram,
            // thermochromic ink, true watermark, padlock icon,
            // microprint border AND microprint signature line,
            // chemically sensitive paper, invisible fluorescent
            // fibers, void pantograph, toner adhesion, consecutive
            // numbering — plus a security thread (b: Wikipedia's
            // cashier's-check construction list). The invented
            // 'watermark-eagle' and 'guilloche-ultra' claims leave.
            // Light green paper stands (a: FDIC alert — a real
            // bank's Deluxe-printed cashier's checks are LIGHT GREEN
            // with microprint signature line and chemical
            // protection); the green primary is the intended tint.
            // The ground corrects to the evidenced safety weave in
            // TEMPLATE_DESIGN. The previous build is preserved as
            // CASHIER_STUDIO.
            securityFeatures: ['watermark', 'holographic-foil', 'thermochromic-ink', 'microprint-border', 'microprint-signature', 'chemical-sensitive-paper', 'uv-fibers', 'void-pantograph', 'secure-padlock', 'toner-adhesion', 'security-thread', 'sequential-numbering']
        }
    },

    CASHIER_STUDIO: {
        name: "Cashier's Check (Certified) (Studio Variant)",
        specs: {
            dimensions: { width: 3.75, height: 8.75, unit: 'inches' },
            margins: { top: 0.5, bottom: 0.5, left: 0.5, right: 0.5 },
            micrPosition: { fromBottom: 0.3125, fromLeft: 0.625 },
            fontFamily: 'Georgia',
            colors: {
                primary: '#1A4D1A',
                secondary: '#FFD700',
                background: '#F0FFF0'
            },
            securityFeatures: ['watermark', 'holographic-foil', 'thermochromic-ink', 'microprint-border', 'microprint-signature', 'chemical-sensitive-paper', 'uv-fibers', 'void-pantograph', 'secure-padlock', 'toner-adhesion', 'security-thread', 'sequential-numbering']
        }
    },

    PAYROLL: {
        name: 'ADP/Paychex Style Payroll',
        specs: {
            dimensions: { width: 3.5, height: 8.5, unit: 'inches' },
            margins: { top: 0.25, bottom: 0.25, left: 0.25, right: 0.25 },
            micrPosition: { fromBottom: 0.3125, fromLeft: 0.625 },
            fontFamily: 'Courier New',
            colors: {
                primary: '#333333',
                secondary: '#666666',
                background: '#FFFFFF'
            },
            // SPECIALTY wave (2026-11-05): an ADP/Paychex-specific
            // stock specification is NOT FOUND (c) after targeted
            // searches — payroll checks print on standard business
            // laser stock, so the set is the programme DEFAULT
            // (Deluxe Standard, flagged in the ledger — a default,
            // NOT evidence). The old list's 'check-stock-watermark'
            // claims a true watermark the standard named list (a)
            // does carry no such feature. The ground corrects from
            // PLAIN to the evidenced safety weave in TEMPLATE_DESIGN
            // — payroll stock IS security stock (the ECHECK plain
            // logic inverted). The previous build is preserved as
            // PAYROLL_STUDIO.
            securityFeatures: ['chemical-sensitive-paper', 'microprint-border', 'security-screentone', 'erasure-protection']
        }
    },

    PAYROLL_STUDIO: {
        name: 'ADP/Paychex Style Payroll (Studio Variant)',
        specs: {
            dimensions: { width: 3.5, height: 8.5, unit: 'inches' },
            margins: { top: 0.25, bottom: 0.25, left: 0.25, right: 0.25 },
            micrPosition: { fromBottom: 0.3125, fromLeft: 0.625 },
            fontFamily: 'Courier New',
            colors: {
                primary: '#333333',
                secondary: '#666666',
                background: '#FFFFFF'
            },
            securityFeatures: ['chemical-sensitive-paper', 'microprint-border', 'security-screentone', 'erasure-protection']
        }
    },

    ECHECK: {
        name: 'Digital eCheck (Email-a-Check)',
        specs: {
            dimensions: { width: 3.5, height: 8.5, unit: 'inches' },
            margins: { top: 0.1, bottom: 0.1, left: 0.1, right: 0.1 },
            micrPosition: { fromBottom: 0.3, fromLeft: 0.6 },
            fontFamily: 'Arial',
            colors: {
                primary: '#000000',
                secondary: '#0066CC',
                background: '#FFFFFF'
            },
            // DIGITAL wave (2026-11-05): an eCheck is an ELECTRONIC
            // instrument, not paper. The canonical form (Deluxe
            // Payment Exchange, (a) echecks.zendesk.com) is created
            // and signed digitally and PRINTED BY THE RECIPIENT "using
            // normal paper in either color or black and white"; its
            // real technology is digital signing plus a verifiable
            // digital fingerprint (VerifyValid lineage, (b)). So the
            // spec list names exactly those two verification facts —
            // both strings already sit in templateSecurity's
            // UNMAPPED_SPEC_STRINGS.verification, so nothing renders
            // from them and no paper feature is claimed. The old
            // list's 'blockchain-hash' and 'timestamp-encryption'
            // were invented claims and are removed ('digital-
            // signature-hash' / 'qr-code-verification' were the same
            // facts in unlisted spellings). The ground corrects to
            // PLAIN stock in TEMPLATE_DESIGN: a woven safety ground
            // would falsely imply safety paper and contradict this
            // template's own back ("NO SECURITY PAPER FEATURES
            // APPLY"). The back itself is UNTOUCHED — the V7
            // horizontal instructions panel stands (excluded scope).
            // Black primary (B&W print) stands; the blue secondary
            // has no evidenced counterpart for a generic instrument →
            // retained, no-evidence-standard. The eCheck's own
            // margins (0.1) and MICR seat (0.3/0.6) stand — they are
            // the 'echeck' layout's seats. The previous build is
            // preserved as ECHECK_STUDIO.
            securityFeatures: ['digital-signature', 'qr-verification']
        }
    },

    ECHECK_STUDIO: {
        name: 'Digital eCheck (Email-a-Check) (Studio Variant)',
        specs: {
            dimensions: { width: 3.5, height: 8.5, unit: 'inches' },
            margins: { top: 0.1, bottom: 0.1, left: 0.1, right: 0.1 },
            micrPosition: { fromBottom: 0.3, fromLeft: 0.6 },
            fontFamily: 'Arial',
            colors: {
                primary: '#000000',
                secondary: '#0066CC',
                background: '#FFFFFF'
            },
            securityFeatures: ['digital-signature', 'qr-verification']
        }
    },

    MONEY_ORDER: {
        name: 'Money Order Style',
        specs: {
            dimensions: { width: 3.0, height: 7.0, unit: 'inches' },
            margins: { top: 0.25, bottom: 0.25, left: 0.25, right: 0.25 },
            micrPosition: { fromBottom: 0.25, fromLeft: 0.5 },
            fontFamily: 'Arial Black',
            colors: {
                primary: '#003366',
                secondary: '#FFD700',
                background: '#FFFACD'
            },
            // SPECIALTY wave (2026-11-05): the modelled instrument
            // is the USPS money order (a: about.usps.com postal
            // bulletins) — Benjamin Franklin watermark repeated
            // top-to-bottom on the left, a dark security thread,
            // pre-printed serial, and stock whose discoloration
            // around the amount indicates alteration (chemically
            // sensitive paper). 'color-shifting-ink' is NOT in the
            // USPS named list and leaves; 'watermark-liberty'
            // normalizes to the generic watermark claim (USPS's
            // device is Franklin, not Liberty — no device name is
            // claimed). The V5 back panel is unchanged (its mapping
            // consumes the watermark + serial strings directly).
            // The previous build is preserved as MONEY_ORDER_STUDIO.
            securityFeatures: ['watermark', 'security-thread', 'sequential-numbering', 'chemical-sensitive-paper']
        }
    },

    MONEY_ORDER_STUDIO: {
        name: 'Money Order Style (Studio Variant)',
        specs: {
            dimensions: { width: 3.0, height: 7.0, unit: 'inches' },
            margins: { top: 0.25, bottom: 0.25, left: 0.25, right: 0.25 },
            micrPosition: { fromBottom: 0.25, fromLeft: 0.5 },
            fontFamily: 'Arial Black',
            colors: {
                primary: '#003366',
                secondary: '#FFD700',
                background: '#FFFACD'
            },
            securityFeatures: ['watermark', 'security-thread', 'sequential-numbering', 'chemical-sensitive-paper']
        }
    },

    TRAVELERS_CHECK: {
        name: "Traveler's Check",
        specs: {
            dimensions: { width: 3.0, height: 6.0, unit: 'inches' },
            margins: { top: 0.25, bottom: 0.25, left: 0.25, right: 0.25 },
            micrPosition: { fromBottom: 0.25, fromLeft: 0.5 },
            fontFamily: 'Times New Roman',
            colors: {
                primary: '#00008B',
                secondary: '#006400',
                background: '#F5F5DC'
            },
            // SPECIALTY wave (2026-11-05): the modelled instrument
            // is the American Express traveler's cheque (a: Amex
            // merchant acceptance guide, web.aexp-static.com) —
            // Centurion watermark, holographic foil, metallic
            // security thread visible on both sides, countersignature
            // process (the VT back's strip note stands on this).
            // 'uv-reactive' is NOT in the Amex named feature list
            // and leaves. The ground takes the engraved guilloche
            // character (b: intricate engraved patterns are the
            // instrument's face character) in TEMPLATE_DESIGN;
            // antique paper, Times face and the dark blue primary
            // stand. The previous build is preserved as
            // TRAVELERS_CHECK_STUDIO.
            securityFeatures: ['dual-signature', 'holographic-strip', 'watermark', 'security-thread']
        }
    },

    TRAVELERS_CHECK_STUDIO: {
        name: "Traveler's Check (Studio Variant)",
        specs: {
            dimensions: { width: 3.0, height: 6.0, unit: 'inches' },
            margins: { top: 0.25, bottom: 0.25, left: 0.25, right: 0.25 },
            micrPosition: { fromBottom: 0.25, fromLeft: 0.5 },
            fontFamily: 'Times New Roman',
            colors: {
                primary: '#00008B',
                secondary: '#006400',
                background: '#F5F5DC'
            },
            securityFeatures: ['dual-signature', 'holographic-strip', 'watermark', 'security-thread']
        }
    },

    COUNTER_CHECK: {
        name: 'Counter Check (Temporary)',
        specs: {
            dimensions: { width: 3.5, height: 8.5, unit: 'inches' },
            margins: { top: 0.25, bottom: 0.25, left: 0.25, right: 0.25 },
            micrPosition: { fromBottom: 0.3125, fromLeft: 0.625 },
            fontFamily: 'Arial',
            colors: {
                primary: '#666666',
                secondary: '#999999',
                background: '#FFFFFF'
            },
            securityFeatures: ['basic-microprinting', 'void-pantograph']
        }
    },

    CERTIFIED: {
        name: 'Certified Bank Check',
        specs: {
            dimensions: { width: 3.5, height: 8.5, unit: 'inches' },
            margins: { top: 0.25, bottom: 0.25, left: 0.25, right: 0.25 },
            micrPosition: { fromBottom: 0.3125, fromLeft: 0.625 },
            fontFamily: 'Times New Roman',
            colors: {
                primary: '#2F4F4F',
                secondary: '#DAA520',
                background: '#FAFAD2'
            },
            // SPECIALTY wave (2026-11-05): a certified check is NOT
            // a distinct secure stock — it is an ORDINARY personal
            // check from the customer's own checkbook which the bank
            // verifies and stamps "Certified" on the face (b:
            // Credit Karma, bill.com, legalclarity, all agreeing).
            // The holographic stamp, embossed seal, guilloche,
            // UV and thread claims were fabrications for this
            // instrument and leave; the set is the bank's standard
            // personal tier (Deluxe Standard, the programme default
            // for a personal check). The ground corrects from
            // marble to the evidenced safety weave and the paper
            // from antique to white in TEMPLATE_DESIGN. The
            // previous build is preserved as CERTIFIED_STUDIO.
            securityFeatures: ['chemical-sensitive-paper', 'microprint-border', 'security-screentone', 'erasure-protection']
        }
    },

    CERTIFIED_STUDIO: {
        name: 'Certified Bank Check (Studio Variant)',
        specs: {
            dimensions: { width: 3.5, height: 8.5, unit: 'inches' },
            margins: { top: 0.25, bottom: 0.25, left: 0.25, right: 0.25 },
            micrPosition: { fromBottom: 0.3125, fromLeft: 0.625 },
            fontFamily: 'Times New Roman',
            colors: {
                primary: '#2F4F4F',
                secondary: '#DAA520',
                background: '#FAFAD2'
            },
            securityFeatures: ['chemical-sensitive-paper', 'microprint-border', 'security-screentone', 'erasure-protection']
        }
    },

    CONTRACTOR: {
        name: 'Contractor/Invoice Check',
        specs: {
            dimensions: { width: 3.5, height: 11.0, unit: 'inches' },
            margins: { top: 0.5, bottom: 3.5, left: 0.25, right: 0.25 },
            micrPosition: { fromBottom: 0.3125, fromLeft: 0.625 },
            fontFamily: 'Arial',
            colors: {
                primary: '#333333',
                secondary: '#666666',
                background: '#FFFFFF'
            },
            // SPECIALTY wave (2026-11-05): business laser stock —
            // the set is the programme DEFAULT (Deluxe Standard,
            // flagged in the ledger). 'stub-perforations' is a
            // FORMAT fact (carried by this template's dimensions
            // and bottom margin), not a security claim, and leaves
            // the security list. The ground corrects from the
            // decorative 'rough' texture to the evidenced safety
            // weave in TEMPLATE_DESIGN. The previous build is
            // preserved as CONTRACTOR_STUDIO.
            securityFeatures: ['chemical-sensitive-paper', 'microprint-border', 'security-screentone', 'erasure-protection']
        }
    },

    CONTRACTOR_STUDIO: {
        name: 'Contractor/Invoice Check (Studio Variant)',
        specs: {
            dimensions: { width: 3.5, height: 11.0, unit: 'inches' },
            margins: { top: 0.5, bottom: 3.5, left: 0.25, right: 0.25 },
            micrPosition: { fromBottom: 0.3125, fromLeft: 0.625 },
            fontFamily: 'Arial',
            colors: {
                primary: '#333333',
                secondary: '#666666',
                background: '#FFFFFF'
            },
            securityFeatures: ['chemical-sensitive-paper', 'microprint-border', 'security-screentone', 'erasure-protection']
        }
    },

    VOUCHER: {
        name: 'Voucher Check (3-Part)',
        specs: {
            dimensions: { width: 3.5, height: 11.0, unit: 'inches' },
            margins: { top: 0.25, bottom: 0.25, left: 0.25, right: 0.25 },
            micrPosition: { fromBottom: 0.3125, fromLeft: 0.625 },
            fontFamily: 'Courier New',
            colors: {
                primary: '#000000',
                secondary: '#333333',
                background: '#FFFFFF'
            },
            // SPECIALTY wave (2026-11-05): a voucher check is
            // standard business laser stock (b: Deluxe's own
            // multipurpose voucher carries chemically sensitive
            // paper + microprinting among its named features) —
            // the set is the programme DEFAULT (Deluxe Standard,
            // flagged in the ledger). 'perforation-lines' is a
            // FORMAT fact of the 3-part form, not a security
            // claim, and leaves. The ground corrects from linen
            // to the evidenced safety weave in TEMPLATE_DESIGN.
            // The previous build is preserved as VOUCHER_STUDIO.
            securityFeatures: ['chemical-sensitive-paper', 'microprint-border', 'security-screentone', 'erasure-protection']
        }
    },

    VOUCHER_STUDIO: {
        name: 'Voucher Check (3-Part) (Studio Variant)',
        specs: {
            dimensions: { width: 3.5, height: 11.0, unit: 'inches' },
            margins: { top: 0.25, bottom: 0.25, left: 0.25, right: 0.25 },
            micrPosition: { fromBottom: 0.3125, fromLeft: 0.625 },
            fontFamily: 'Courier New',
            colors: {
                primary: '#000000',
                secondary: '#333333',
                background: '#FFFFFF'
            },
            securityFeatures: ['chemical-sensitive-paper', 'microprint-border', 'security-screentone', 'erasure-protection']
        }
    },

    INDUSTRIAL_PAYROLL: {
        name: 'Industrial Heavy Business',
        specs: {
            dimensions: { width: 3.5, height: 8.5, unit: 'inches' },
            margins: { top: 0.25, bottom: 0.25, left: 0.25, right: 0.25 },
            micrPosition: { fromBottom: 0.3125, fromLeft: 0.625 },
            fontFamily: 'Helvetica',
            colors: {
                primary: '#333333',
                secondary: '#FF6600',
                background: '#F0F0F0'
            },
            // SPECIALTY wave (2026-11-05): business laser stock —
            // the set is the programme DEFAULT (Deluxe Standard,
            // flagged in the ledger). The old list named
            // 'toner-adhesion', a HIGH-SECURITY tier feature, as a
            // face claim for standard stock; it leaves with the
            // correction (and its ground texture namesake corrects
            // to the evidenced safety weave in TEMPLATE_DESIGN —
            // the COMERICA defect class). The previous build is
            // preserved as INDUSTRIAL_PAYROLL_STUDIO.
            securityFeatures: ['chemical-sensitive-paper', 'microprint-border', 'security-screentone', 'erasure-protection']
        }
    },

    INDUSTRIAL_PAYROLL_STUDIO: {
        name: 'Industrial Heavy Business (Studio Variant)',
        specs: {
            dimensions: { width: 3.5, height: 8.5, unit: 'inches' },
            margins: { top: 0.25, bottom: 0.25, left: 0.25, right: 0.25 },
            micrPosition: { fromBottom: 0.3125, fromLeft: 0.625 },
            fontFamily: 'Helvetica',
            colors: {
                primary: '#333333',
                secondary: '#FF6600',
                background: '#F0F0F0'
            },
            securityFeatures: ['chemical-sensitive-paper', 'microprint-border', 'security-screentone', 'erasure-protection']
        }
    },
    AMEX_CENTURION: {
        // OWNER RULING rebuild (fidelity programme, 2026-11-05):
        // Princewill ruled the remaining flagged templates take
        // their banks' ORIGINAL designs. No Centurion bank check
        // exists (programme research, re-confirmed 2026-11-05):
        // check writing exists only on American Express Rewards
        // Checking (a, AENB Deposit Agreement), the checkbooks are
        // provided by third-party vendor Harland Clarke (a,
        // americanexpress.com's own Business Checking disclosures:
        // "Third-party vendor Harland Clarke provides checkbooks"),
        // and the order flow lands on Harland Clarke's site with
        // "a cool American Express logo" on the checks (b,
        // eyewitness review of the Rewards Checking order flow) —
        // the ONLY Amex-specific front furniture evidenced. The
        // Centurion head / black prestige styling / hologram cluster
        // belongs to Amex TRAVELERS CHEQUES (a, Amex acceptance
        // guide) — a different instrument, already in the library
        // as TRAVELERS_CHECK. So the main entry is now the plain
        // original: an ordinary HC personal check with the Amex
        // logo imprint, and its display name is the honest plain
        // name (the template id keeps AMEX_CENTURION for
        // compatibility). Imprint ink is American Express Blue
        // #016FD0 (b, multiply attested: designpieces' named
        // palette, logo measurements, provider brand data); the
        // wordmark itself is custom artwork (b), so the imprint
        // face is the standard sans of the other HC personal
        // rebuilds, recorded no-evidence-standard. The neutral
        // grey secondary stands (the brand's supporting grey
        // family); the 'Platinum' accent was part of the Centurion
        // fiction and takes the plain standard white. The
        // pre-rebuild Centurion build is preserved verbatim as
        // AMEX_CENTURION_STUDIO below.
        name: 'American Express',
        specs: {
            dimensions: { width: 3.5, height: 8.5, unit: 'inches' },
            margins: { top: 0.25, bottom: 0.25, left: 0.25, right: 0.25 },
            micrPosition: { fromBottom: 0.3125, fromLeft: 0.5 },
            fontFamily: 'Arial',
            colors: {
                primary: '#016FD0',
                secondary: '#444444',
                accent: '#FFFFFF',
                background: '#FFFFFF'
            },
            securityFeatures: ['security-weave', 'microprinting', 'security-screentone', 'fugitive-ink']
        }
    },

    // Preserved pre-rebuild build (OWNER RULING rebuild,
    // 2026-11-05): the Centurion prestige concept exactly as it
    // shipped — black/platinum palette, Copperplate face, guilloche
    // ground — kept verbatim under the Studio Variant name (the
    // programme pattern). Its security set is the live
    // corrected HC Standard Personal set (the correction is a
    // product fact, not a design choice).
    AMEX_CENTURION_STUDIO: {
        name: 'Centurion High-Prestige (Studio Variant)',
        specs: {
            dimensions: { width: 3.5, height: 8.5, unit: 'inches' },
            margins: { top: 0.25, bottom: 0.25, left: 0.25, right: 0.25 },
            micrPosition: { fromBottom: 0.3125, fromLeft: 0.5 },
            fontFamily: 'Copperplate',
            colors: {
                primary: '#000000',
                secondary: '#444444',
                accent: '#E5E4E2', // Platinum
                background: '#FFFFFF'
            },
            securityFeatures: ['security-weave', 'microprinting', 'security-screentone', 'fugitive-ink']
        }
    },

    // ============== CLASSIC / GENERIC ==============

    GENERIC: {
        name: 'Classic Premium',
        specs: {
            dimensions: { width: 3.5, height: 8.5, unit: 'inches' },
            margins: { top: 0.25, bottom: 0.25, left: 0.25, right: 0.25 },
            micrPosition: { fromBottom: 0.3125, fromLeft: 0.625 },
            fontFamily: 'Arial',
            colors: {
                primary: '#2C3E50',
                secondary: '#3498DB',
                accent: '#E74C3C',
                background: '#FFFFFF'
            },
            securityFeatures: ['microprinting', 'void-pantograph', 'guilloche-pattern', 'chemical-sensitive-paper']
        }
    },

    CLASSIC_BLUE: {
        name: 'Classic Blue',
        specs: {
            dimensions: { width: 3.5, height: 8.5, unit: 'inches' },
            margins: { top: 0.25, bottom: 0.25, left: 0.25, right: 0.25 },
            micrPosition: { fromBottom: 0.3125, fromLeft: 0.625 },
            fontFamily: 'Times New Roman',
            colors: {
                primary: '#1E3A5F',
                secondary: '#4A90D9',
                background: '#F0F8FF'
            },
            // FINAL wave rebuild (2026-11-05): the Harland Clarke
            // "Classics" safety design (Guardian Safety Blue) — the
            // Harland Clarke Standard Personal set. 'security-border'
            // was an unmapped construction string, not a feature.
            securityFeatures: ['security-weave', 'microprinting', 'security-screentone', 'fugitive-ink']
        }
    },

    // Preserved pre-rebuild build (FINAL wave, 2026-11-05).
    CLASSIC_BLUE_STUDIO: {
        name: 'Classic Blue (Studio Variant)',
        specs: {
            dimensions: { width: 3.5, height: 8.5, unit: 'inches' },
            margins: { top: 0.25, bottom: 0.25, left: 0.25, right: 0.25 },
            micrPosition: { fromBottom: 0.3125, fromLeft: 0.625 },
            fontFamily: 'Times New Roman',
            colors: {
                primary: '#1E3A5F',
                secondary: '#4A90D9',
                background: '#F0F8FF'
            },
            securityFeatures: ['security-weave', 'microprinting', 'security-screentone', 'fugitive-ink']
        }
    },

    CLASSIC_GREEN: {
        name: 'Classic Green',
        specs: {
            dimensions: { width: 3.5, height: 8.5, unit: 'inches' },
            margins: { top: 0.25, bottom: 0.25, left: 0.25, right: 0.25 },
            micrPosition: { fromBottom: 0.3125, fromLeft: 0.625 },
            fontFamily: 'Times New Roman',
            colors: {
                primary: '#1A4D1A',
                secondary: '#2E8B57',
                background: '#F0FFF0'
            },
            // FINAL wave rebuild (2026-11-05): Guardian Safety Green
            // — the Harland Clarke Standard Personal set (see
            // CLASSIC_BLUE).
            securityFeatures: ['security-weave', 'microprinting', 'security-screentone', 'fugitive-ink']
        }
    },

    // Preserved pre-rebuild build (FINAL wave, 2026-11-05).
    CLASSIC_GREEN_STUDIO: {
        name: 'Classic Green (Studio Variant)',
        specs: {
            dimensions: { width: 3.5, height: 8.5, unit: 'inches' },
            margins: { top: 0.25, bottom: 0.25, left: 0.25, right: 0.25 },
            micrPosition: { fromBottom: 0.3125, fromLeft: 0.625 },
            fontFamily: 'Times New Roman',
            colors: {
                primary: '#1A4D1A',
                secondary: '#2E8B57',
                background: '#F0FFF0'
            },
            securityFeatures: ['security-weave', 'microprinting', 'security-screentone', 'fugitive-ink']
        }
    },

    CLASSIC_MAROON: {
        name: 'Executive Maroon',
        specs: {
            dimensions: { width: 3.5, height: 8.5, unit: 'inches' },
            margins: { top: 0.25, bottom: 0.25, left: 0.25, right: 0.25 },
            micrPosition: { fromBottom: 0.3125, fromLeft: 0.625 },
            fontFamily: 'Georgia',
            colors: {
                primary: '#800000',
                secondary: '#B22222',
                background: '#FFF5F5'
            },
            // FINAL wave rebuild (2026-11-05): the maroon classic
            // (Harland Clarke Sheffield Maroon; Deluxe's herringbone
            // maroon) — the Harland Clarke Standard Personal set.
            // The old guilloche claim leaves with the guilloche
            // ground; the colourway is the design's expressible
            // identity (the Sheffield torch scene is catalogue
            // artwork the tokens cannot express).
            securityFeatures: ['security-weave', 'microprinting', 'security-screentone', 'fugitive-ink']
        }
    },

    // Preserved pre-rebuild build (FINAL wave, 2026-11-05).
    CLASSIC_MAROON_STUDIO: {
        name: 'Executive Maroon (Studio Variant)',
        specs: {
            dimensions: { width: 3.5, height: 8.5, unit: 'inches' },
            margins: { top: 0.25, bottom: 0.25, left: 0.25, right: 0.25 },
            micrPosition: { fromBottom: 0.3125, fromLeft: 0.625 },
            fontFamily: 'Georgia',
            colors: {
                primary: '#800000',
                secondary: '#B22222',
                background: '#FFF5F5'
            },
            securityFeatures: ['security-weave', 'microprinting', 'security-screentone', 'fugitive-ink']
        }
    },

    PARCHMENT: {
        name: 'Parchment Style',
        specs: {
            dimensions: { width: 3.5, height: 8.5, unit: 'inches' },
            margins: { top: 0.25, bottom: 0.25, left: 0.25, right: 0.25 },
            micrPosition: { fromBottom: 0.3125, fromLeft: 0.625 },
            fontFamily: 'Palatino Linotype',
            colors: {
                primary: '#5C4033',
                secondary: '#8B7355',
                background: '#FAEBD7'
            },
            // FINAL wave rebuild (2026-11-05): parchment stock (a
            // named catalogue product — "Securiguard Parchment" (b))
            // carries the programme DEFAULT set for the direct-printer
            // house tier (Deluxe Standard — flagged default, not
            // evidence). 'parchment-texture' was a paper fact, not a
            // security claim (the VOUCHER precedent); the parchment
            // character lives in the ground/paper tokens, which stand.
            // Spec-list-only change — no variant (US_TREASURY_OFFICIAL
            // precedent).
            securityFeatures: ['chemical-sensitive-paper', 'microprint-border', 'security-screentone', 'erasure-protection']
        }
    },

    MARBLE: {
        name: 'Marble Texture',
        specs: {
            dimensions: { width: 3.5, height: 8.5, unit: 'inches' },
            margins: { top: 0.25, bottom: 0.25, left: 0.25, right: 0.25 },
            micrPosition: { fromBottom: 0.3125, fromLeft: 0.625 },
            fontFamily: 'Georgia',
            colors: {
                primary: '#4A4A4A',
                secondary: '#808080',
                background: '#F5F5F5'
            },
            // FINAL wave rebuild (2026-11-05): marble stock (Deluxe
            // sells blue/green marble as a premium catalogue design
            // (b)) carries the programme DEFAULT set for the house
            // tier (Deluxe Standard — flagged default). 'marble-texture'
            // was a paper fact and 'guilloche-pattern' a claim the
            // marble design does not draw (its named character is the
            // marble pattern in the ground token, which stands).
            // Spec-list-only change — no variant (US_TREASURY_OFFICIAL
            // precedent).
            securityFeatures: ['chemical-sensitive-paper', 'microprint-border', 'security-screentone', 'erasure-protection']
        }
    },

    // ============== INTERNATIONAL STYLES ==============

    CANADIAN: {
        name: 'Canadian Bank Style',
        specs: {
            dimensions: { width: 3.5, height: 8.5, unit: 'inches' },
            margins: { top: 0.25, bottom: 0.25, left: 0.25, right: 0.25 },
            micrPosition: { fromBottom: 0.3125, fromLeft: 0.625 },
            fontFamily: 'Arial',
            colors: {
                primary: '#C41E3A',
                secondary: '#003366',
                background: '#FFFFFF'
            },
            // FINAL wave rebuild (2026-11-05): the Canadian format
            // (Payments Canada Standard 006 (a) + Canadian standard-
            // stock security core (b)): microprinting, watermark,
            // chemical spotting, security screen — the Deluxe
            // Standard set plus the watermark, with 'bilingual-text'
            // kept as the format's evidenced non-rendering fact
            // (ECHECK precedent). 'maple-leaf-watermark' normalizes
            // to the generic 'watermark' (the MONEY_ORDER device
            // precedent — the renderer draws a watermark, not a maple
            // leaf); 'security-thread' was an unmapped construction
            // string and leaves.
            securityFeatures: ['chemical-sensitive-paper', 'microprint-border', 'security-screentone', 'erasure-protection', 'watermark', 'bilingual-text']
        }
    },

    // Preserved pre-rebuild build (FINAL wave, 2026-11-05).
    CANADIAN_STUDIO: {
        name: 'Canadian Bank Style (Studio Variant)',
        specs: {
            dimensions: { width: 3.5, height: 8.5, unit: 'inches' },
            margins: { top: 0.25, bottom: 0.25, left: 0.25, right: 0.25 },
            micrPosition: { fromBottom: 0.3125, fromLeft: 0.625 },
            fontFamily: 'Arial',
            colors: {
                primary: '#C41E3A',
                secondary: '#003366',
                background: '#FFFFFF'
            },
            securityFeatures: ['chemical-sensitive-paper', 'microprint-border', 'security-screentone', 'erasure-protection', 'watermark', 'bilingual-text']
        }
    },

    UK_STYLE: {
        name: 'UK Cheque Style',
        specs: {
            dimensions: { width: 3.37, height: 6.29, unit: 'inches' },
            margins: { top: 0.25, bottom: 0.25, left: 0.25, right: 0.25 },
            micrPosition: { fromBottom: 0.3125, fromLeft: 0.625 },
            fontFamily: 'Times New Roman',
            colors: {
                primary: '#002366',
                secondary: '#8B0000',
                background: '#FFFFF0'
            },
            // FINAL wave (2026-11-05): 'crown-watermark' normalizes
            // to the generic 'watermark' — CBS1 paper IS watermarked
            // (a heavy all-over design (b), UK security printers /
            // C&CCC Standard 3.1/3.3 context) but a CROWN device is
            // (c) NOT FOUND; the renderer draws a watermark, not a
            // crown (the MONEY_ORDER device precedent). Normalization
            // only — the rendered set, tokens and the plain V7L back
            // are unchanged, so no variant (US_TREASURY_OFFICIAL
            // precedent).
            securityFeatures: ['watermark', 'sterling-symbol', 'microprinting', 'guilloche-pattern']
        }
    },

    KAREN_ART_STUDIO_PAYROLL: {
        name: 'Karen Art Studio (Classic 3-Part)',
        specs: {
            dimensions: { width: 11.0, height: 8.5, unit: 'inches' }, // 3-part layout
            margins: { top: 0.2, bottom: 0.2, left: 0.2, right: 0.2 },
            micrPosition: { fromBottom: 0.4, fromLeft: 1.25 },
            fontFamily: 'Arial',
            colors: {
                primary: '#0055a4',
                secondary: '#333333',
                background: '#dae9f7'
            },
            securityFeatures: ['blue-safety-band', 'microprint-border', 'lock-icon-security', 'dual-stub-layout']
        },
        fields: [
            { name: 'company_header', x: 0.5, y: 0.4, width: 3.0, fontSize: 11, bold: true },
            { name: 'bank_header', x: 5.5, y: 0.4, width: 3.0, fontSize: 11, bold: true },
            { name: 'check_number_top', x: 7.8, y: 0.3, width: 0.5, fontSize: 13, bold: true },
            { name: 'date', x: 7.5, y: 0.8, width: 1.0, fontSize: 12 },
            { name: 'payee_name', x: 1.5, y: 1.3, width: 4.0, fontSize: 12 },
            { name: 'amount_numeric', x: 7.8, y: 1.3, width: 1.0, fontSize: 12, bold: true },
            { name: 'amount_words', x: 0.5, y: 1.6, width: 7.0, fontSize: 11 },
            { name: 'payee_address_block', x: 1.2, y: 2.0, width: 3.0, fontSize: 10 },
            { name: 'memo', x: 1.0, y: 3.0, width: 2.0, fontSize: 10 },
            { name: 'signature', x: 6.5, y: 2.8, width: 2.0 }
        ]
    }
};

// Template categories for UI organization
export const templateCategories = {
    major: ['BOFA', 'CHASE', 'CHASE_PRIVATE', 'WELLS', 'CITI', 'USAA', 'PNC', 'TD', 'CAPITAL_ONE', 'US_BANK', 'AMEX_CENTURION', 'BOFA_STUDIO', 'CHASE_PRIVATE_STUDIO', 'WELLS_STUDIO', 'CITI_STUDIO', 'PNC_STUDIO', 'CAPITAL_ONE_STUDIO', 'US_BANK_STUDIO', 'AMEX_CENTURION_STUDIO'],
    regional: ['REGIONS', 'TRUIST', 'BMO', 'HUNTINGTON', 'FIFTH_THIRD', 'KEYBANK', 'REGIONS_STUDIO', 'TRUIST_STUDIO', 'HUNTINGTON_STUDIO', 'FIFTH_THIRD_STUDIO', 'KEYBANK_STUDIO', 'CITIZENS', 'ZIONS', 'COMERICA', 'FIRST_HORIZON', 'M_AND_T', 'CITIZENS_STUDIO', 'ZIONS_STUDIO', 'COMERICA_STUDIO', 'FIRST_HORIZON_STUDIO', 'M_AND_T_STUDIO', 'ANB', 'ANB_SKY'],
    creditUnion: ['NAVY_FCU', 'PENFED', 'BECU', 'ALLIANT', 'GOLDEN1', 'NAVY_FCU_STUDIO', 'PENFED_STUDIO', 'BECU_STUDIO', 'ALLIANT_STUDIO', 'GOLDEN1_STUDIO'],
    specialty: ['CASHIER', 'PAYROLL', 'INDUSTRIAL_PAYROLL', 'ECHECK', 'MONEY_ORDER', 'TRAVELERS_CHECK', 'COUNTER_CHECK', 'CERTIFIED', 'CONTRACTOR', 'VOUCHER', 'KAREN_ART_STUDIO_PAYROLL',
        // SPECIALTY wave (2026-11-05): the pre-rebuild builds,
        // preserved as Studio Variants (GREENFI_MINT pattern).
        // COUNTER_CHECK and KAREN_ART_STUDIO_PAYROLL have no
        // variant: the counter check was researched and stands
        // unchanged, and the Karen design is a house design whose
        // established build stands (ledger records both).
        // US_TREASURY_OFFICIAL has no variant either — its rebuild
        // changed no effective-design field (BMO precedent).
        'CASHIER_STUDIO', 'PAYROLL_STUDIO', 'INDUSTRIAL_PAYROLL_STUDIO', 'MONEY_ORDER_STUDIO', 'TRAVELERS_CHECK_STUDIO', 'CERTIFIED_STUDIO', 'CONTRACTOR_STUDIO', 'VOUCHER_STUDIO'],
    classic: ['GENERIC', 'CLASSIC_BLUE', 'CLASSIC_GREEN', 'CLASSIC_MAROON', 'PARCHMENT', 'MARBLE', 'CLASSIC_BLUE_STUDIO', 'CLASSIC_GREEN_STUDIO', 'CLASSIC_MAROON_STUDIO'],
    international: ['CANADIAN', 'UK_STYLE', 'CANADIAN_STUDIO'],
    digital: ['ALLY', 'DISCOVER', 'ECHECK', 'GREENFI', 'GREENFI_MINT', 'ALLY_STUDIO', 'DISCOVER_STUDIO', 'ECHECK_STUDIO']
};

// Get all template IDs
export const getAllTemplateIds = (): string[] => Object.keys(bankSpecificTemplates);

// Get template by category
export const getTemplatesByCategory = (category: keyof typeof templateCategories): BankTemplate[] =>
    templateCategories[category].map(id => bankSpecificTemplates[id]).filter(Boolean);
